/*
 * Kunstmühle: Mühlenspiel „Der kleine Müller“ – Kinder führen einen Tag lang die Kunstmühle:
 * Korn von Bauern aus dem 40-km-Umkreis annehmen, Probe ziehen, reinigen, (Dinkel schälen),
 * mahlen, sieben, abfüllen und Bestellungen mit dem richtigen Mehl ausliefern. Fünf Level
 * erzählen die Geschichte der Mühle von 1923 bis heute; der Mühlenkater Krümel und der Wassertropfen Plitsch begleiten.
 * Datenquelle: JSON aus sections/km-muehlenspiel.liquid (Bestellungen, Lernkarten, Produkte).
 * Spielstand nur im Browser (localStorage „km-muehlenspiel“), keine Cookies.
 * Statistik: anonyme Spielereignisse per sendBeacon an die Shop-App (App-Proxy, Adresse aus der Section,
 * leer = aus) – nur mit Zustimmung zu Statistik im Cookie-Banner. ?km-statistik=test zeigt alles in der Konsole.
 * Shop-Brücke am Tagesende: Malbuch über window.kmAddItems (km-relaunch.js), Produktlinks.
 * Aufruf: <km-muehlenspiel> mit <script type="application/json" data-km-spiel-config>.
 */
(() => {
  if (customElements.get('km-muehlenspiel')) return;

  const KEY = 'km-muehlenspiel';
  const load = () => {
    try {
      return JSON.parse(localStorage.getItem(KEY)) || {};
    } catch (e) {
      return {};
    }
  };
  const save = (s) => {
    try {
      localStorage.setItem(KEY, JSON.stringify(s));
    } catch (e) {}
  };
  const esc = (s) =>
    String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);
  const pick = (list) => list[Math.floor(Math.random() * list.length)];
  const shuffle = (list) => {
    const a = list.slice();
    for (let i = a.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [a[i], a[j]] = [a[j], a[i]];
    }
    return a;
  };
  const today = () => new Date().toISOString().slice(0, 10);
  const reduced = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const wait = (ms) => new Promise((r) => setTimeout(r, reduced() ? Math.min(ms, 300) : ms));

  /* ---------- Spielwelt ---------- */

  // Fünf Level = die Geschichte der Mühle (Fakten von den Seiten Geschichte, Technik, Nachhaltigkeit)
  const LEVELS = [
    null,
    {
      jahr: '1923',
      titel: 'Ab nach Fahlenbach',
      text: 'Die alte Schleifmühle in Wolnzach ist zu klein. Darum zieht die Familie 1923 nach Fahlenbach. Deine erste Aufgabe: Weizen mahlen für die Bäckerei.',
      neu: 'Neu: Weizen',
      grains: ['weizen'],
      kunden: ['baecker'],
      packs: ['tuete', 'sack'],
    },
    {
      jahr: '1980',
      titel: 'Der Mühlenladen geht auf',
      text: 'Überall in Deutschland machen Mühlen zu. Unsere bleibt, baut um – und eröffnet 1980 einen eigenen Mühlenladen. Jetzt kommen auch Familien vorbei.',
      neu: 'Neu: Roggen und Familien im Mühlenladen',
      grains: ['weizen', 'roggen'],
      kunden: ['baecker', 'familie'],
      packs: ['tuete', 'sack'],
    },
    {
      jahr: '2005',
      titel: 'Die Kamera-Maschine',
      text: 'Ein Farbausleser kommt in die Mühle: Er schaut mit Kameras auf jedes Korn und pustet dunkle Körner weg. Und es gibt viel mehr Silos.',
      neu: 'Neu: Rotkorn, Farbausleser und BigBags',
      grains: ['weizen', 'roggen', 'rotkorn'],
      kunden: ['baecker', 'familie'],
      packs: ['tuete', 'sack', 'bigbag'],
      kamera: true,
    },
    {
      jahr: '2019',
      titel: 'Der Dinkelschäler',
      text: 'Dinkel hat einen festen Mantel, den Spelz. Seit 2019 schält die Mühle den Dinkel selbst – vom Korn bis zum Mehl alles in Fahlenbach.',
      neu: 'Neu: Dinkel und die Schäl-Station',
      grains: ['weizen', 'roggen', 'rotkorn', 'dinkel'],
      kunden: ['baecker', 'familie'],
      packs: ['tuete', 'sack', 'bigbag'],
      kamera: true,
    },
    {
      jahr: 'Heute',
      titel: 'Die Kunstmühle heute',
      text: 'Wasserrad und Sonnendach machen über 80 % vom Strom selbst. Fische schwimmen über die Fischtreppe, aus Dinkelspelzen werden Pellets – und Pakete gehen an den Onlineshop.',
      neu: 'Neu: alle Getreide, Pakete, Solardach und Fischtreppe',
      grains: ['weizen', 'roggen', 'rotkorn', 'dinkel'],
      kunden: ['baecker', 'familie', 'paket'],
      packs: ['tuete', 'sack', 'bigbag'],
      kamera: true,
      solar: true,
    },
  ];

  const GRAINS = {
    weizen: { name: 'Weizen', sieve: ['vollkorn', '1050', '550', '405'] },
    roggen: { name: 'Roggen', sieve: ['vollkorn', '1150', '997'] },
    rotkorn: { name: 'Rotkorn', sieve: ['vollkorn'] },
    dinkel: { name: 'Dinkel', sieve: ['vollkorn', '1050', '630'] },
  };
  const FLOURS = {
    vollkorn: { name: 'Vollkorn', hint: 'das ganze Korn', tone: 'f-vk' },
    1150: { name: 'Type 1150', hint: 'dunkel', tone: 'f-11' },
    1050: { name: 'Type 1050', hint: 'dunkel', tone: 'f-10' },
    997: { name: 'Type 997', hint: 'mittel', tone: 'f-99' },
    630: { name: 'Type 630', hint: 'hell', tone: 'f-63' },
    550: { name: 'Type 550', hint: 'hell', tone: 'f-55' },
    405: { name: 'Type 405', hint: 'ganz hell', tone: 'f-40' },
  };

  // Erfundene Bauern aus den vier Landkreisen im 40-km-Umkreis
  const FARMERS = [
    { name: 'Bauer Sepp', kreis: 'Landkreis Pfaffenhofen', km: 6, grains: ['weizen', 'roggen'], ang: 200, witz: 'Mein Hund Bello hat aufgepasst, dass kein Korn runterfällt.' },
    { name: 'Bäuerin Resi', kreis: 'Landkreis Neuburg-Schrobenhausen', km: 28, grains: ['roggen', 'weizen'], ang: 260, witz: 'Was macht ein Roggenkorn bei Regen? Es wird ein Bröckchen nass!' },
    { name: 'Bauer Michl', kreis: 'Landkreis Ingolstadt', km: 30, grains: ['weizen', 'dinkel'], ang: 320, witz: 'Mein Traktor ist so laut, die Hühner haben Ohrenschützer.' },
    { name: 'Bio-Bauer Toni', kreis: 'Landkreis Eichstätt', km: 36, grains: ['dinkel', 'rotkorn', 'weizen'], ang: 290, witz: 'Mein Traktor ist fast so alt wie die Mühle!' },
  ];

  const CUSTOMERS = {
    baecker: {
      name: 'Bäckerei Maier',
      wo: 'Bäckerei',
      packs: ['sack', 'bigbag'],
      frage: 'Die Bäckerei Maier backt jeden Tag Hunderte Semmeln und Brote. Sie braucht ganz viel Mehl auf einmal!',
      tip: 'Eine Bäckerei braucht viel Mehl: einen großen Sack oder einen BigBag.',
    },
    familie: {
      name: 'Familie Huber',
      wo: 'Mühlenladen',
      packs: ['tuete'],
      frage: 'Familie Huber kauft im Mühlenladen ein und backt zu Hause. Das Mehl muss in ihren Küchenschrank passen.',
      tip: 'Familien im Mühlenladen nehmen eine Tüte mit nach Hause.',
    },
    paket: {
      name: 'Lena',
      wo: 'Onlineshop',
      packs: ['tuete'],
      frage: 'Lena hat im Onlineshop bestellt. Ihr Mehl kommt mit der Post – es muss in ein Paket passen.',
      tip: 'Ins Paket kommt eine Tüte, damit die Post es tragen kann.',
    },
  };
  const PACKS = {
    tuete: ['Tüte, 1 kg', 'für zu Hause'],
    sack: ['Sack, 25 kg', 'für eine Backstube'],
    bigbag: ['BigBag, bis 1.000 kg', 'für große Bäckereien'],
  };

  // Spielarten: „Kleiner Müller“ ohne Uhr und mit Hilfen, „Meister-Müller“ schneller und ohne Mehlskala-Hilfe
  const MODES = {
    klein: { name: 'Kleiner Müller', info: 'ohne Uhr, mit Hilfen', orders: -1, time: 0, wet: 0.5, clean: 3 },
    normal: { name: 'Müller', info: 'mit Uhr', orders: 0, time: 1, wet: 1, clean: 0 },
    meister: { name: 'Meister-Müller', info: 'schneller, ohne Hilfen', orders: 1, time: 0.8, wet: 1.3, clean: -1.5 },
  };

  const CARDS = [
    { id: 'weizen', name: 'Weizen', text: 'Aus Weizen wird das hellste Mehl: Type 405 für Kuchen und Plätzchen.' },
    { id: 'roggen', name: 'Roggen', text: 'Roggen ist genügsam, verträgt kalte Winter und mag Sauerteig.' },
    { id: 'rotkorn', name: 'Rotkorn', text: 'Ein fast vergessener Weizen. Sein Mehl bleibt auch gebacken rot-violett.' },
    { id: 'dinkel', name: 'Dinkel', text: 'Dinkel steckt in einem festen Spelz. Den schält die Mühle in Fahlenbach selbst ab.' },
    { id: 'emmer', name: 'Emmer', text: 'Emmer ist ein Urgetreide – Menschen bauen ihn seit Tausenden von Jahren an.', ur: true },
    { id: 'einkorn', name: 'Einkorn', text: 'Einkorn hat nur ein Korn pro Ährchen. Daher kommt sein Name.', ur: true },
    { id: 'waldstaudenroggen', name: 'Waldstaudenroggen', text: 'Ein Urroggen mit vielen Namen, zum Beispiel Johannisroggen. Er schmeckt süßlich.', ur: true },
    { id: 'kamut', name: 'Khorasan', text: 'Khorasan-Weizen hat besonders große, lange Körner.', ur: true },
  ];

  /* ---------- Zeichnungen (Hausfarben über Klassen in km-muehlenspiel.css) ---------- */

  const svg = (vb, body, cls = '') => `<svg class="${cls}" viewBox="${vb}" aria-hidden="true" focusable="false">${body}</svg>`;

  const FACE = (x1, x2, y, mood, withMouth = true) => {
    const mouth = !withMouth
      ? ''
      : mood === 'oh'
        ? `<ellipse class="fn" cx="${(x1 + x2) / 2}" cy="${y + 13}" rx="3" ry="3.6"/>`
        : mood === 'sad'
          ? `<path class="o n" d="M${x1 + 2} ${y + 15}q${(x2 - x1) / 2 - 2} -6 ${x2 - x1 - 4} 0"/>`
          : `<path class="o n" d="M${x1} ${y + 10}q${(x2 - x1) / 2} 9 ${x2 - x1} 0"/>`;
    return `<ellipse class="fw ol" cx="${x1}" cy="${y}" rx="4.6" ry="5.4"/><ellipse class="fw ol" cx="${x2}" cy="${y}" rx="4.6" ry="5.4"/><circle class="fn" cx="${x1 + 1}" cy="${y + 1}" r="2.4"/><circle class="fn" cx="${x2 + 1}" cy="${y + 1}" r="2.4"/><circle class="fw" cx="${x1 + 1.8}" cy="${y}" r=".8"/><circle class="fw" cx="${x2 + 1.8}" cy="${y}" r=".8"/><ellipse class="fz wange" cx="${x1 - 5}" cy="${y + 8}" rx="4" ry="2.4"/><ellipse class="fz wange" cx="${x2 + 5}" cy="${y + 8}" rx="4" ry="2.4"/>${mouth}`;
  };

  const ART = {
    // Krümel, der Mühlenkater – mit Mehlkrümeln an den Pfoten
    kruemel: (mood = 'happy') =>
      svg(
        '0 0 90 110',
        `<g class="o"><path class="fg n schwanz" d="M62 96q24 0 22-22-1-10-8-12" stroke-width="7"/><path class="fg" d="M20 104q-6-36 25-44 31 8 25 44z"/><path class="fm" d="M33 104q-1-24 12-30 13 6 12 30z"/><ellipse class="fm" cx="30" cy="104" rx="9" ry="5"/><ellipse class="fm" cx="60" cy="104" rx="9" ry="5"/><path class="fg" d="M17 26l6-22 16 14zM73 26l-6-22-16 14z"/><path class="fz" d="M22 22l3-11 7 7zM68 22l-3-11-7 7z" opacity=".55"/><ellipse class="fg" cx="45" cy="40" rx="29" ry="25"/></g><path class="fh streifen" d="M38 17l2 9M45 15v10M52 17l-2 9M17 40h7M66 40h7M24 76l6 3M66 76l-6 3"/><ellipse class="fm" cx="45" cy="52" rx="13" ry="9"/><path class="fz o" d="M42 46h6l-3 4z"/><path class="ol n" d="M30 50l-12-2M30 54l-12 2M60 50l12-2M60 54l12 2"/><g class="o"><path class="fe" d="M70 12q8 2 7 10-8 1-10-6z"/><path class="n" d="M72 18l-4-8"/></g>${FACE(36, 54, 38, mood, false)}${mood === 'oh' ? '<ellipse class="fn" cx="45" cy="57" rx="2.6" ry="3"/>' : mood === 'sad' ? '<path class="o n" d="M40 59q5-4 10 0"/>' : '<path class="o n" d="M39 54q3 4 6 0 3 4 6 0"/>'}`,
        'kmg-figur kmg-kruemel'
      ),
    // Plitsch, ein Wassertropfen aus der Ilm – treibt das Wasserrad an
    plitsch: (mood = 'happy') =>
      svg(
        '0 0 70 92',
        `<g class="o n"><path d="M27 78l-3 9h-6M43 78l3 9h6M14 54q-9-2-10-11M56 54q9-3 10-12"/></g><g class="o"><path class="fb" d="M35 4C44 22 62 38 60 58c-2 16-12 22-25 22S12 74 10 58C8 38 26 22 35 4z"/></g><path class="fw plitsch-licht" d="M20 50q0-14 10-26-3 14-4 28z"/>${FACE(28, 43, 50, mood)}`,
        'kmg-figur kmg-plitsch'
      ),
    traktor: (foreign = false) =>
      foreign
        ? svg(
            '0 0 120 64',
            `<g class="o"><rect class="fb" x="4" y="12" width="78" height="36" rx="3"/><path class="fl" d="M82 22h20l12 14v12H82z"/><path class="fw" d="M88 26h12l8 10H88z"/><circle class="fn" cx="22" cy="52" r="8"/><circle class="fn" cx="96" cy="52" r="8"/><circle class="fn" cx="66" cy="52" r="8"/></g><text class="fw kmg-svgtext" x="43" y="35" text-anchor="middle">900 km</text>`,
            'kmg-fahrzeug'
          )
        : svg(
            '0 0 120 64',
            `<g class="o"><path class="fs" d="M4 30h52l-4 18H8z"/><path class="fg" d="M8 30q22-16 44 0z"/><path class="fz" d="M60 24h28l4 14h16v12H60z"/><path class="fw" d="M66 10h18v16H66z"/><path class="n" d="M64 10h22"/><circle class="fn" cx="74" cy="50" r="12"/><circle class="fl" cx="74" cy="50" r="5"/><circle class="fn" cx="104" cy="54" r="7"/><circle class="fn" cx="22" cy="52" r="6"/><circle class="fn" cx="44" cy="52" r="6"/></g>`,
            'kmg-fahrzeug'
          ),
    // Ähren: Farbe, Grannen und Kornform je Getreide (auch die Urgetreide der Sammelkarten)
    grain: (g) => {
      const look = {
        weizen: ['fg', 'kurz', 4, 3.6],
        roggen: ['fe', 'lang', 4, 3],
        rotkorn: ['fz', '', 4, 3.6],
        dinkel: ['fs', '', 4, 3.6],
        emmer: ['fh', 'lang', 3, 4],
        einkorn: ['fs', 'kurz', 5, 2.8],
        waldstaudenroggen: ['fn', 'lang', 5, 2.6],
        kamut: ['fg', 'lang', 3, 4.6],
      }[g] || ['fg', '', 4, 3.6];
      const [tone, grannen, rows, w] = look;
      const step = 26 / rows;
      const awns =
        grannen === 'lang'
          ? `<path class="ol n" d="M20 9V0M14 13L8 3M26 13l6-10M14 20L7 11M26 20l7-9"/>`
          : grannen === 'kurz'
            ? `<path class="ol n" d="M20 8V2M14 13l-4-6M26 13l4-6M14 21l-5-5M26 21l5-5"/>`
            : '';
      let k = '';
      for (let i = 0; i < rows; i++) {
        const y = 12 + i * step;
        k += `<ellipse class="${tone} ol" cx="16" cy="${y}" rx="${w}" ry="${w * 1.4}" transform="rotate(-25 16 ${y})"/><ellipse class="${tone} ol" cx="24" cy="${y}" rx="${w}" ry="${w * 1.4}" transform="rotate(25 24 ${y})"/>`;
      }
      return svg('0 0 40 40', `<path class="o n" d="M20 39V20"/>${awns}${k}`, 'kmg-ico');
    },
    herzen: (n) =>
      `<span class="kmg-herzen" aria-label="${n} von 5 Herzen">${[1, 2, 3, 4, 5]
        .map((i) => svg('0 0 20 18', `<path class="${i <= n ? 'fz' : 'fl'} ol" d="M10 17L2.5 9.5A4.6 4.6 0 0110 3.6a4.6 4.6 0 017.5 5.9z"/>`, 'kmg-herz'))
        .join('')}</span>`,
    blitz: () => svg('0 0 20 24', '<path class="fg ol" d="M12 1L3 14h6l-2 9 10-14h-6z"/>', 'kmg-blitz'),
    pack: (p) => {
      if (p === 'tuete')
        return svg('0 0 60 60', `<g class="o"><path class="fm" d="M14 14h32l3 42H11z"/><path class="fl" d="M14 14l4-8h24l4 8"/><rect class="fb" x="19" y="28" width="22" height="14" rx="2"/></g><text class="fw kmg-svgtext s" x="30" y="38" text-anchor="middle">1 kg</text>`, 'kmg-ico');
      if (p === 'sack')
        return svg('0 0 60 60', `<g class="o"><path class="fl" d="M12 20q-4 36 6 37h24q10-1 6-37z"/><path class="fl" d="M18 20q-2-8 4-12h16q6 4 4 12z"/><path class="n" d="M18 13h24"/></g><text class="fn kmg-svgtext s" x="30" y="44" text-anchor="middle">25 kg</text>`, 'kmg-ico');
      return svg('0 0 60 60', `<g class="o"><path class="n" d="M14 14q3-10 8 0M38 14q5-10 8 0"/><rect class="fb" x="8" y="14" width="44" height="42" rx="4"/><path class="fm" d="M20 4h20l-4 10H24z"/></g><text class="fw kmg-svgtext s" x="30" y="40" text-anchor="middle">BigBag</text>`, 'kmg-ico');
    },
    // Backwaren (Bildauswahl der Bestell-Blöcke)
    bake: (b, flat = false) => {
      const P = {
        kuchen: `<g class="o"><path class="fg" d="M10 50q0-26 22-30 22 4 22 30z"/><path class="fs" d="M10 50h44v6H10z"/><path class="fm" d="M14 34q6 4 9-2 4 6 9 0 4 6 9 0 4 5 9-1-6-12-18-14-12 2-18 17z"/></g><circle class="fz" cx="32" cy="16" r="4"/>`,
        plaetzchen: `<g class="o"><circle class="fg" cx="20" cy="40" r="13"/><circle class="fs" cx="44" cy="40" r="13"/><path class="fg" d="M32 6l4 8 9 1-7 6 2 9-8-5-8 5 2-9-7-6 9-1z"/></g><circle class="fn" cx="16" cy="36" r="1.8"/><circle class="fn" cx="24" cy="43" r="1.8"/><circle class="fz" cx="44" cy="40" r="3"/>`,
        pizza: `<g class="o"><circle class="fg" cx="32" cy="34" r="25"/><circle class="fz" cx="32" cy="34" r="19"/></g><circle class="fm" cx="24" cy="28" r="5"/><circle class="fm" cx="40" cy="38" r="5"/><circle class="fm" cx="28" cy="44" r="4"/><path class="fe" d="M38 24q6-4 8 2-4 4-8-2z"/>`,
        semmel: `<g class="o"><path class="fg" d="M8 44q0-26 24-26t24 26q-24 10-48 0z"/><path class="n" d="M18 28q14 6 28 0M32 22v22"/></g>`,
        hefezopf: `<g class="o"><ellipse class="fg" cx="16" cy="38" rx="10" ry="12"/><ellipse class="fs" cx="30" cy="34" rx="10" ry="13"/><ellipse class="fg" cx="44" cy="38" rx="10" ry="12"/><ellipse class="fs" cx="54" cy="40" rx="6" ry="8"/></g><circle class="fm" cx="28" cy="28" r="1.6"/><circle class="fm" cx="34" cy="34" r="1.6"/><circle class="fm" cx="18" cy="34" r="1.6"/>`,
        mischbrot: `<g class="o"><path class="fh" d="M6 46q0-24 26-26t26 26q0 6-26 6T6 46z"/><path class="n" d="M18 30l6 8M28 26l6 8M38 26l6 8"/></g>`,
        vollkornbrot: `<g class="o"><path class="fn" d="M8 26h48v26H8z"/><path class="fh" d="M8 26q24-12 48 0z"/></g><g class="fs"><circle cx="16" cy="36" r="1.6"/><circle cx="26" cy="44" r="1.6"/><circle cx="36" cy="34" r="1.6"/><circle cx="46" cy="44" r="1.6"/><circle cx="20" cy="22" r="1.6"/><circle cx="40" cy="22" r="1.6"/></g>`,
        bauernbrot: `<g class="o"><circle class="fh" cx="32" cy="36" r="24"/></g><path class="fm" d="M18 26q14-10 28 0-14-4-28 0zM14 40q6-2 8 4M42 44q6-6 10-2"/><path class="o n" d="M20 34l24 4M22 44l20 2"/>`,
        brezel: `<path class="fh brezel" d="M32 48C14 60 4 42 14 30s26-6 18 10M32 48c18 12 28-6 18-18S24 24 32 40M22 50l20-12M42 50L22 38"/><g class="fw"><circle cx="14" cy="34" r="1.6"/><circle cx="48" cy="30" r="1.6"/><circle cx="32" cy="22" r="1.6"/><circle cx="40" cy="50" r="1.6"/></g>`,
        spaetzle: `<g class="o"><path class="fb" d="M6 34h52q-2 22-26 22T6 34z"/></g><path class="o n spaetzle" d="M12 34q4-8 8 0t8 0 8 0 8 0 8 0M16 28q4-6 8 0t8 0 8 0 8 0"/>`,
        dinkelbrot: `<g class="o"><path class="fh" d="M4 44q2-22 28-24 26 2 28 24-2 8-28 8T4 44z"/><path class="n" d="M14 38q18-10 36 0"/></g><path class="fs" d="M44 14q4 6 0 12-4-6 0-12z"/>`,
        waffel: `<g class="o"><path class="fz" d="M32 56L8 32a12 12 0 0124-14 12 12 0 0124 14z"/></g><path class="o n waffel" d="M16 22l28 28M24 16l26 26M14 34l18-18M20 42l22-22M28 48l20-20"/>`,
      };
      return svg('0 0 64 64', P[b] || P.semmel, `kmg-ico kmg-bake${flat ? ' is-flat' : ''}`);
    },
    kunde: (k) => {
      if (k === 'baecker')
        return svg('0 0 60 60', `<g class="o"><path class="fm" d="M8 26h44v30H8z"/><path class="fz" d="M4 28l26-20 26 20z"/><path class="fh" d="M24 40h12v16H24z"/><rect class="fb" x="12" y="34" width="8" height="8" rx="1"/><rect class="fb" x="40" y="34" width="8" height="8" rx="1"/></g><path class="brezel" d="M30 24c-6 4-9-2-6-4s6-1 6 3c0-4 3-5 6-3s0 8-6 4" stroke-width="2.6"/>`, 'kmg-ico');
      if (k === 'familie')
        return svg('0 0 60 60', `<g class="o"><path class="fm" d="M10 28h40v28H10z"/><path class="fe" d="M6 30l24-20 24 20z"/><path class="fh" d="M26 40h10v16H26z"/></g><path class="fz o" d="M30 34c-4-4-9 0-5 4l5 4 5-4c4-4-1-8-5-4z" transform="translate(-14 -6) scale(.8)"/>`, 'kmg-ico');
      return svg('0 0 60 60', `<g class="o"><path class="fs" d="M8 22l22-10 22 10v26L30 58 8 48z"/><path class="fl" d="M8 22l22 10 22-10M30 32v26"/></g><path class="fb" d="M14 25l22-10 4 2-22 10z"/>`, 'kmg-ico');
    },
    walzen: () =>
      svg(
        '0 0 160 130',
        `<g class="o"><path class="fl" d="M54 4h52l-12 22H66z"/><rect class="fh" x="30" y="26" width="100" height="70" rx="6"/><rect class="fm" x="40" y="36" width="80" height="48" rx="4"/><path class="fl" d="M70 96h20l-4 18H74z"/></g><g class="walze w1"><circle class="fs o" cx="64" cy="60" r="16"/><path class="ol n" d="M64 44v32M48 60h32M53 49l22 22M75 49L53 71"/></g><g class="walze w2"><circle class="fs o" cx="96" cy="60" r="16"/><path class="ol n" d="M96 44v32M80 60h32M85 49l22 22M107 49L85 71"/></g><g class="koerner fg"><circle cx="72" cy="12" r="3"/><circle cx="82" cy="16" r="3"/><circle cx="90" cy="10" r="3"/></g>`,
        'kmg-walzen'
      ),
    ofen: () =>
      svg(
        '0 0 160 120',
        `<g class="o"><path class="fz" d="M10 114V60q0-50 70-50t70 50v54z"/><path class="fn" d="M44 114V74q0-26 36-26t36 26v40z"/></g><path class="fg feuer" d="M60 112q-6-16 8-24-2 12 8 6 0-12 10-16-4 14 6 18 8 6 2 16z"/><path class="fl" d="M30 40h8v8h-8zM122 40h8v8h-8zM50 26h8v8h-8zM102 26h8v8h-8z" opacity=".35"/>`,
        'kmg-ofen'
      ),
    karte: (ang, foreign) => {
      const r = 46;
      const a = ((ang || 0) * Math.PI) / 180;
      const fx = 70 + Math.cos(a) * r * 0.75;
      const fy = 62 + Math.sin(a) * r * 0.75;
      const mark = foreign
        ? `<path class="o n gestrichelt" d="M6 10Q30 30 44 44"/><circle class="fb o" cx="8" cy="10" r="6"/>`
        : `<path class="o n gestrichelt" d="M${fx} ${fy}L70 62"/><circle class="fz o" cx="${fx}" cy="${fy}" r="6"/>`;
      return svg(
        '0 0 140 124',
        `<circle class="fe kreis" cx="70" cy="62" r="${r}"/><circle class="o n gestrichelt" cx="70" cy="62" r="${r}"/>${mark}<path class="fm o" d="M62 66v-9l8-7 8 7v9z"/><text class="fn kmg-svgtext s" x="70" y="122" text-anchor="middle">40 km um die Mühle</text>`,
        'kmg-landkarte'
      );
    },
    feuchte: () =>
      svg(
        '0 0 160 96',
        `<path class="fe" d="M14 84a66 66 0 0189-62l-23 62z"/><path class="fb" d="M103 22a66 66 0 0143 62H80z" opacity=".75"/><path class="o n" d="M14 84a66 66 0 01132 0"/><g class="nadel"><path class="fn" d="M80 84l-3-58h6z"/><circle class="fn" cx="80" cy="84" r="7"/></g><text class="fn kmg-svgtext" x="34" y="72">trocken</text><text class="fw kmg-svgtext" x="128" y="72" text-anchor="end">nass</text>`,
        'kmg-feuchte'
      ),
    stern: (on = true) => svg('0 0 24 24', `<path class="${on ? 'fg' : 'fl'} ol" d="M12 2l3 6.4 7 .8-5.2 4.8 1.4 7L12 17.6 5.8 21l1.4-7L2 9.2l7-.8z"/>`, 'kmg-stern'),
  };

  // Mühlen-Szene: Hallertau mit Hopfengarten, Mühle mit Elevatorturm, Wasserrad und Brücke.
  // Die Sonne wandert mit der Tageszeit; je Level kommen Mühlenladen (1980), Silos und Solardach dazu.
  const SCENE = (lvl, season, crop = false) => {
    const L = LEVELS[lvl];
    const silos = lvl >= 3 ? 3 : lvl >= 2 ? 2 : 1;
    let siloSvg = '';
    for (let i = silos - 1; i >= 0; i--) {
      const x = 446 + i * 30;
      siloSvg += `<g class="o"><rect class="fl" x="${x}" y="${92 + i * 4}" width="28" height="${92 - i * 4}" rx="3"/><path class="fm" d="M${x} ${95 + i * 4}l14-12 14 12z"/></g><path class="ol n" d="M${x} ${120 + i * 4}h28M${x} ${150 + i * 4}h28"/>`;
    }
    siloSvg += `<path class="ol n" d="M450 104v80M456 104v80M450 112h6M450 122h6M450 132h6M450 142h6M450 152h6M450 162h6M450 172h6"/>`;
    let hopfen = '<path class="ol n" d="M18 108h104"/>';
    // Hopfengarten: Stangen mit Drähten, an denen die Reben hochranken
    for (let x = 22; x <= 118; x += 16) {
      hopfen += `<path class="ol n" d="M${x} 108v42"/>`;
      for (let y = 113; y <= 146; y += 5) hopfen += `<circle class="fe hopfenblatt" cx="${x + (y % 10 ? 2.6 : -2.6)}" cy="${y}" r="3.2"/>`;
      hopfen += `<circle class="fg" cx="${x + 3}" cy="124" r="1.6"/><circle class="fg" cx="${x - 3}" cy="136" r="1.6"/>`;
    }
    const laden =
      lvl >= 2
        ? `<g class="o"><path class="fm" d="M350 146h46v38h-46z"/><path class="fh" d="M364 160h18v24h-18z"/><path class="fw" d="M346 146h54l-4 10h-46z"/></g><path class="fz" d="M352 146h7l-1 10h-6zM366 146h7v10h-7zM380 146h7l1 10h-7zM394 146h4l-2 10h-3z"/><text class="fn kmg-svgtext xs" x="373" y="143" text-anchor="middle">LADEN</text>`
        : '';
    const solar = L.solar
      ? `<path class="fb o" d="M254 74l36 24-8 5-36-24z"/><path class="ol n solarlinien" d="M263 80l-8 5M272 86l-8 5M281 92l-8 5"/>`
      : '';
    const fische = L.solar
      ? `<path class="o n" d="M560 214v-6h14v-6h14v-6h14"/><g transform="translate(586 206)"><g class="fisch"><path class="fz o" d="M0 0q8-7 16 0-8 7-16 0zM0 0l-6-5v10z"/></g></g>`
      : '';
    const advent = season === 'advent';
    const schnee = advent
      ? `<path class="fw o" d="M188 106l60-40 60 40-6 2-54-34-54 34z"/><path class="fw o" d="M296 58l30-22 30 22-5 2-25-17-25 17z"/><g class="fw schnee">${Array.from({ length: 30 }, (_, i) => `<circle cx="${(i * 97) % 640}" cy="${(i * 53) % 160}" r="${1.4 + (i % 3)}"/>`).join('')}</g>`
      : '';
    const spokes = [0, 45, 90, 135].map((a) => `<path class="o n" d="M0-30V30" transform="rotate(${a})"/>`).join('');
    const paddles = [0, 30, 60, 90, 120, 150, 180, 210, 240, 270, 300, 330].map((a) => `<path class="fh o" d="M-1.5-22h3v-15h-3z" transform="rotate(${a})"/>`).join('');
    return `<svg class="kmg-szene" viewBox="0 0 640 230" preserveAspectRatio="${crop ? 'xMidYMid slice' : 'xMidYMid meet'}" aria-hidden="true" focusable="false">
      <defs><linearGradient id="kmgHimmel" x1="0" y1="0" x2="0" y2="1"><stop offset="0" class="stop-blau"/><stop offset="1" class="stop-weiss"/></linearGradient></defs>
      <rect width="640" height="230" fill="url(#kmgHimmel)"/>
      <g class="sonne" transform="translate(70 60)"><circle class="fg sonnenschein" r="26"/><circle class="fg" r="17"/></g>
      <g class="wolke w1"><ellipse class="fw" cx="150" cy="34" rx="26" ry="10"/><ellipse class="fw" cx="168" cy="28" rx="18" ry="12"/><ellipse class="fw" cx="134" cy="30" rx="12" ry="9"/></g>
      <g class="wolke w2"><ellipse class="fw" cx="470" cy="24" rx="30" ry="10"/><ellipse class="fw" cx="488" cy="17" rx="18" ry="11"/><ellipse class="fw" cx="452" cy="20" rx="13" ry="9"/></g>
      <g class="voegel"><path class="ol n" d="M392 44q4-4 8 0q4-4 8 0M412 56q3-3 6 0q3-3 6 0"/></g>
      <path class="fe" opacity=".35" d="M0 132q80-40 170-22t170-6 180-14 120 18v122H0z"/>
      <circle class="fe" opacity=".5" cx="520" cy="108" r="9"/><circle class="fe" opacity=".5" cx="534" cy="104" r="11"/><circle class="fe" opacity=".5" cx="210" cy="110" r="8"/>
      <path class="fe" opacity=".6" d="M0 150q120-38 250-12t240-8 150 10v90H0z"/>
      ${hopfen}
      <path class="fs" d="M0 176q160-22 320-8t320-6v68H0z"/>
      <path class="fg n furchen" d="M0 186q160-18 320-6t320-8M0 194q160-16 320-4t320-8"/>
      <g class="o"><path class="fh" d="M592 186v-26M626 186v-22"/><circle class="fe" cx="592" cy="150" r="16"/><circle class="fe" cx="604" cy="160" r="12"/><circle class="fe" cx="626" cy="152" r="14"/></g>
      <path class="ol n" d="M500 182h80M506 176v10M522 176v10M538 176v10M554 176v10M570 176v10"/>
      ${siloSvg}
      <g class="o"><path class="fm" d="M300 58h52v126h-52z"/><path class="fz" d="M296 60l30-24 30 24z"/><path class="fh n" d="M352 66h14v8"/><path class="n" d="M364 74v10"/><path class="fl" d="M359 84h10l1 9h-12z"/></g>
      <g class="fb o fenster"><rect x="310" y="70" width="10" height="13" rx="2"/><rect x="332" y="70" width="10" height="13" rx="2"/><rect x="310" y="96" width="10" height="13" rx="2"/><rect x="332" y="96" width="10" height="13" rx="2"/><rect x="310" y="122" width="10" height="13" rx="2"/><rect x="332" y="122" width="10" height="13" rx="2"/></g>
      <g class="o"><path class="fm" d="M196 104h104v80H196z"/><path class="fz" d="M188 106l60-40 60 40z"/></g>
      <path class="ziegellinien" d="M218 86h60M206 96h84M233 76h30"/>
      ${solar}
      <g class="fb o fenster"><rect x="208" y="114" width="16" height="16" rx="2"/><rect x="240" y="114" width="16" height="16" rx="2"/><rect x="272" y="114" width="16" height="16" rx="2"/><rect x="208" y="156" width="16" height="16" rx="2"/></g>
      <path class="fenstersprossen" d="M216 114v16M208 122h16M248 114v16M240 122h16M280 114v16M272 122h16M216 156v16M208 164h16"/>
      <path class="fz" d="M206 172h20v4h-20z"/><circle class="fz" cx="210" cy="170" r="2.2"/><circle class="fg" cx="216" cy="169" r="2.2"/><circle class="fz" cx="222" cy="170" r="2.2"/>
      <g class="o"><rect class="fw" x="200" y="134" width="96" height="14" rx="3"/><path class="fh" d="M238 184v-22q10-10 20 0v22z"/></g>
      <text class="fn kmg-svgtext xs" x="248" y="144" text-anchor="middle">KUNSTMÜHLE HOFMEIR</text>
      <g class="o"><path class="fl" d="M266 184q-2-12 5-14h8q7 2 5 14z"/><path class="fl" d="M280 184q-2-10 4-12h7q6 2 4 12z"/><path class="fs" d="M272 172q-2-10 5-12h7q7 2 5 12z"/></g>
      <g class="huhn"><g class="o"><path class="fw" d="M312 186q-10-1-10-9 0-7 8-7 2-6 8-5 4 1 3 7l3 2-3 1q2 8-9 11z"/></g><path class="fz" d="M316 164l2-3 2 3 2-2v4h-6z"/><circle class="fn" cx="317" cy="168" r="1"/><path class="fg" d="M324 170l4 1-4 2z"/></g>
      ${laden}
      <path class="fb" d="M0 202q80-10 160 0t160 0 160 0 160 0v28H0z"/>
      <g class="wellen"><path class="n" d="M14 214q10-4 20 0M100 220q10-4 20 0M210 214q10-4 20 0M330 220q10-4 20 0M430 214q10-4 20 0M540 220q10-4 20 0M620 214q10-4 20 0"/></g>
      <g class="o"><path class="fh" d="M396 204q30-20 60 0h4v6h-68v-6z"/><path class="n" d="M398 194q28-16 56 0M404 196v8M426 188v8M448 196v8"/></g>
      <path class="fh o" d="M190 186h10v-6h-10z"/><g transform="translate(166 188)"><g class="rad">${paddles}<circle class="n o" r="30"/><circle class="n o" r="22"/>${spokes}<circle class="fh o" r="6"/></g></g>
      <g class="fw spritzer"><circle cx="134" cy="206" r="2.4"/><circle cx="140" cy="200" r="1.8"/><circle cx="196" cy="204" r="2"/><circle cx="190" cy="210" r="1.6"/></g>
      ${fische}${schnee}
      ${crop ? '' : `<text class="fn kmg-svgtext jahr" x="16" y="26">${esc(L.jahr)}</text>`}
    </svg>`;
  };

  /* ---------- Ton (WebAudio, nur nach Tipp) ---------- */
  let audio = null;
  const tone = (on, notes) => {
    if (!on) return;
    try {
      audio = audio || new (window.AudioContext || window.webkitAudioContext)();
      let t = audio.currentTime;
      for (const [f, d, type = 'triangle'] of notes) {
        const o = audio.createOscillator();
        const g = audio.createGain();
        o.type = type;
        o.frequency.value = f;
        g.gain.setValueAtTime(0.0001, t);
        g.gain.exponentialRampToValueAtTime(0.12, t + 0.02);
        g.gain.exponentialRampToValueAtTime(0.0001, t + d);
        o.connect(g).connect(audio.destination);
        o.start(t);
        o.stop(t + d + 0.02);
        t += d * 0.8;
      }
    } catch (e) {}
  };
  const SOUND = {
    tap: [[660, 0.06]],
    gut: [[523, 0.12], [659, 0.12], [784, 0.2]],
    schade: [[392, 0.18, 'sine'], [330, 0.28, 'sine']],
    mahl: [[140, 0.1, 'sawtooth']],
    stern: [[880, 0.08], [1175, 0.16]],
  };

  /* ---------- Spiel ---------- */

  class KmMuehlenspiel extends HTMLElement {
    connectedCallback() {
      if (this.kmReady) return;
      this.kmReady = true;
      try {
        this.cfg = JSON.parse(this.querySelector('[data-km-spiel-config]').textContent);
      } catch (e) {
        return;
      }
      this.s = Object.assign({ level: 1, stars: {}, cards: [], ur: 0, factIdx: 0 }, load());
      if (this.s.sound === undefined) this.s.sound = !!this.cfg.sound;
      this.statInit();
      this.innerHTML = `<div class="kmg" data-kmg>
        <div class="kmg-hud" data-hud hidden></div>
        <div class="kmg-buehne" data-stage></div>
        <div class="kmg-panel" data-panel hidden><div class="kmg-panel__inner" data-panel-inner role="dialog" aria-modal="false" aria-live="polite"></div></div>
      </div>
      <div class="kmg-unten" data-below hidden></div>`;
      this.root = this.querySelector('[data-kmg]');
      this.stage = this.querySelector('[data-stage]');
      this.hud = this.querySelector('[data-hud]');
      this.panelEl = this.querySelector('[data-panel]');
      this.below = this.querySelector('[data-below]');
      this.panelInner = this.querySelector('[data-panel-inner]');
      this.addEventListener('click', (e) => this.onClick(e));
      document.addEventListener('visibilitychange', () => {
        if (document.hidden && this.day && !this.day.over) this.setPause(true);
        if (document.hidden) this.statSpielzeit();
      });
      window.addEventListener('pagehide', () => {
        if (this.day && !this.day.over) this.statAbbruch();
        this.statSpielzeit();
      });
      this.menu();
    }

    show(html) {
      this.stage.innerHTML = html;
    }

    // Längere Inhalte (Anleitung, Sammelkarten, Shop-Tipps) stehen unter der Spielbox im Seitenfluss
    showBelow(html, scroll = true) {
      this.below.innerHTML = html;
      this.below.hidden = false;
      if (scroll) {
        this.below.scrollIntoView({ block: 'start', behavior: reduced() ? 'auto' : 'smooth' });
        const focus = this.below.querySelector('h2, h3');
        if (focus) {
          focus.setAttribute('tabindex', '-1');
          focus.focus({ preventScroll: true });
        }
      }
    }
    hideBelow() {
      this.below.hidden = true;
      this.below.innerHTML = '';
    }
    act_nachOben() {
      this.hideBelow();
      this.root.scrollIntoView({ block: 'nearest', behavior: reduced() ? 'auto' : 'smooth' });
      const focus = this.root.querySelector('[data-focus]');
      if (focus) focus.focus({ preventScroll: true });
    }

    persist() {
      save(this.s);
    }

    play(name) {
      tone(this.s.sound, SOUND[name] || SOUND.tap);
    }

    say(text) {
      if (!('speechSynthesis' in window) || !text) return;
      window.speechSynthesis.cancel();
      const u = new SpeechSynthesisUtterance(text);
      u.lang = 'de-DE';
      u.rate = 0.95;
      window.speechSynthesis.speak(u);
    }

    season() {
      const m = new Date().getMonth() + 1;
      return m === 12 ? 'advent' : m === 7 || m === 8 ? 'ernte' : '';
    }

    // Klicks zentral: data-act="name" ruft this.act_name(button)
    onClick(e) {
      const btn = e.target.closest('[data-act]');
      if (!btn || !this.contains(btn) || btn.disabled) return;
      const fn = this[`act_${btn.dataset.act}`];
      if (typeof fn === 'function') {
        if (btn.dataset.act !== 'clean') this.play('tap');
        fn.call(this, btn, e);
      }
    }

    /* ----- Statistik (anonym, nur mit Einwilligung) ----- */
    // Keine Namen, keine Kunden- oder Geräte-IDs: nur eine Zufalls-ID pro Seitenaufruf, damit die App
    // Ereignisse eines Besuchs zusammenzählen kann. Felder und Auswertung: siehe Schnittstellen-Beschreibung.
    statInit() {
      this.sid = Math.random().toString(36).slice(2, 10) + Date.now().toString(36).slice(-4);
      this.statTest = /[?&]km-statistik=test\b/.test(location.search);
      this.spielSek = 0;
      if (this.cfg.statistik) this.consent();
      this.addEventListener('click', (e) => {
        const a = e.target.closest('a[data-stat]');
        if (a) this.track(a.dataset.stat, a.dataset.statName ? { name: a.dataset.statName } : {});
      });
      this.track('besuch', { neu: !this.s.intro && !this.s.played, level: this.s.level });
    }
    consent() {
      const allowed = () => {
        try {
          return !!window.Shopify.customerPrivacy.analyticsProcessingAllowed();
        } catch (e) {
          return false;
        }
      };
      if (window.Shopify?.customerPrivacy) return Promise.resolve(allowed());
      if (!window.Shopify?.loadFeatures) return Promise.resolve(false);
      // Die Zustimmung kann sich während des Besuchs ändern – nur das Laden wird gemerkt
      this.consentLoad ||= new Promise((r) => window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], () => r()));
      return this.consentLoad.then(allowed);
    }
    track(e, data = {}) {
      const ev = Object.assign({ v: 1, e, sid: this.sid, modus: this.mode() }, data);
      if (this.statTest) console.info('[Mühlenspiel-Statistik]', ev);
      const url = this.cfg.statistik;
      if (!url) return;
      this.consent().then((ok) => {
        if (!ok) return;
        const body = JSON.stringify(ev);
        let sent = false;
        try {
          sent = !!navigator.sendBeacon?.(url, new Blob([body], { type: 'text/plain' }));
        } catch (err) {}
        if (!sent) fetch(url, { method: 'POST', body, keepalive: true, credentials: 'same-origin', headers: { 'Content-Type': 'text/plain' } }).catch(() => {});
      });
    }
    // Gespielte Sekunden seit der letzten Meldung (beim Verlassen oder Wegwechseln der Seite)
    statSpielzeit() {
      const sek = Math.round(this.spielSek);
      if (sek < 1) return;
      this.spielSek = 0;
      this.track('spielzeit', { sek });
    }
    statAbbruch() {
      const d = this.day;
      this.track('tag_abbruch', { level: d.level, sek: Math.round(d.real || 0), erledigt: d.orders.filter((o) => o.done).length, bestellungen: d.orders.length });
    }

    /* ----- Panels ----- */
    panel(html, { cls = '', hold = false } = {}) {
      this.panelInner.className = `kmg-panel__inner ${cls}`;
      this.panelInner.innerHTML = html;
      this.panelEl.hidden = false;
      this.holding = hold;
      const focus = this.panelInner.querySelector('[data-focus]') || this.panelInner.querySelector('button, a, input');
      if (focus) focus.focus({ preventScroll: true });
      this.panelEl.scrollTop = 0;
      if (this.fingerOn()) this.point(this.panelInner.querySelector('[data-act="probe"], [data-hold], [data-act="walze"], [data-act="fertig"], [data-act="schliessen"].kmg-btn--gross, [data-act="weiter"]'));
      // Spielbox ganz ins Bild holen (die Kopfzeile des Shops ist per scroll-margin berücksichtigt)
      requestAnimationFrame(() => this.root.scrollIntoView({ block: 'nearest', behavior: reduced() ? 'auto' : 'smooth' }));
    }
    closePanel() {
      this.panelEl.hidden = true;
      this.panelInner.innerHTML = '';
      this.holding = false;
      if (this.day && !this.day.over) this.renderHof();
    }
    bubble(who, text, mood) {
      return `<div class="kmg-sprech"><div class="kmg-sprech__figur">${who === 'kruemel' ? ART.kruemel(mood) : ART.plitsch(mood)}</div><p class="kmg-sprech__text" data-say>${esc(text)}</p><button type="button" class="kmg-vorlesen" data-act="vorlesen" aria-label="Vorlesen">${svg('0 0 24 24', '<path class="fn" d="M4 9h4l5-4v14l-5-4H4z"/><path class="o n" d="M16 9q2 3 0 6M18.5 7q4 5 0 10"/>')}</button></div>`;
    }
    act_vorlesen(btn) {
      const box = btn.closest('.kmg-sprech, .kmg-panel__inner, [data-kmg]');
      const el = box && box.querySelector('[data-say]');
      if (el) this.say(el.textContent);
    }

    /* ----- Menü ----- */
    menu() {
      if (this.day && !this.day.over) this.statAbbruch();
      this.panelEl.hidden = true;
      this.holding = false;
      this.stopDay();
      clearTimeout(this.miniTimer);
      clearTimeout(this.kameraTimer);
      this.mini = null;
      this.day = null;
      this.hud.hidden = true;
      const done = (l) => this.s.stars[l] || 0;
      const levels = [1, 2, 3, 4, 5]
        .map((l) => {
          const open = l <= this.s.level;
          const st = [1, 2, 3].map((i) => ART.stern(i <= done(l))).join('');
          return `<li><button type="button" class="kmg-level${open ? '' : ' is-zu'}" data-act="level" data-level="${l}" ${open ? '' : 'disabled'}><span class="kmg-level__jahr">${esc(LEVELS[l].jahr)}</span><span class="kmg-level__name">${esc(LEVELS[l].titel)}</span><span class="kmg-level__nr">Level ${l}</span><span class="kmg-level__sterne" aria-label="${done(l)} von 3 Sternen">${open ? st : 'noch zu'}</span></button></li>`;
        })
        .join('');
      const cont = Math.min(this.s.level, 5);
      this.show(`<div class="kmg-menue">
        <div class="kmg-menue__kopf">${SCENE(cont, this.season())}<div class="kmg-menue__figuren">${ART.kruemel()}${ART.plitsch()}</div></div>
        <h2 class="kmg-titel">Willkommen in der Kunstmühle!</h2>
        <p class="kmg-lead">Hilf Kater Krümel und Wassertropfen Plitsch: Nimm Korn von den Bauern an, mahle es zu Mehl und backe für alle, die bestellt haben.</p>
        <button type="button" class="kmg-btn kmg-btn--gross" data-act="${this.s.intro ? 'level' : 'intro'}" data-level="${cont}" data-focus>${this.s.intro ? `Weiterspielen: ${esc(LEVELS[cont].jahr)}` : 'Los geht’s!'}</button>
        <ul class="kmg-levels" role="list">${levels}</ul>
        <div class="kmg-modus" role="group" aria-label="Spielart">${Object.keys(MODES)
          .map((m) => `<button type="button" class="kmg-modus__knopf" data-act="modus" data-modus="${m}" aria-pressed="${this.mode() === m}"><b>${MODES[m].name}</b><small>${MODES[m].info}</small></button>`)
          .join('')}</div>
        <div class="kmg-menue__leiste">
          <button type="button" class="kmg-btn kmg-btn--leise" data-act="hilfe">So geht’s</button>
          <button type="button" class="kmg-btn kmg-btn--leise" data-act="karten">Sammelkarten (${this.s.cards.length}/${CARDS.length})</button>
          ${this.s.stars[1] ? '<button type="button" class="kmg-btn kmg-btn--leise" data-act="urkunde">Urkunde drucken</button>' : ''}
          <button type="button" class="kmg-btn kmg-btn--leise" data-act="ton" aria-pressed="${this.s.sound}">Ton: ${this.s.sound ? 'an' : 'aus'}</button>
          <button type="button" class="kmg-btn kmg-btn--leise" data-act="ausmalen">Ausmalbild</button>
          <button type="button" class="kmg-btn kmg-btn--leise" data-act="eltern">Für Erwachsene</button>
        </div>
      </div>`);
    }
    mode() {
      return MODES[this.s.modus] ? this.s.modus : 'normal';
    }
    act_modus(btn) {
      this.s.modus = btn.dataset.modus;
      this.persist();
      this.root.querySelectorAll('[data-act="modus"]').forEach((b) => b.setAttribute('aria-pressed', b.dataset.modus === this.s.modus));
    }
    act_menu() {
      this.closePanel();
      this.menu();
    }
    act_ton(btn) {
      this.s.sound = !this.s.sound;
      this.persist();
      if (btn.closest('.kmg-hud')) {
        this.renderHud(true);
        return this.hud.querySelector('[data-act="ton"]').focus();
      }
      btn.textContent = `Ton: ${this.s.sound ? 'an' : 'aus'}`;
      btn.setAttribute('aria-pressed', this.s.sound);
      this.play('gut');
    }

    act_intro() {
      this.panelEl.hidden = true;
      this.holding = false;
      this.introStep = 0;
      this.showIntro();
    }
    showIntro() {
      const steps = [
        ['plitsch', 'Hallo! Ich bin Plitsch, ein Wassertropfen aus der Ilm, die an unserer Mühle vorbeifließt. Ich drehe das Wasserrad. Und das ist Krümel, unser Mühlenkater.'],
        ['kruemel', 'Miau, ich bin Krümel – wegen der Mehlkrümel an meinen Pfoten! Die Kunstmühle gehört Familie Hofmeir, schon in der 4. Generation. Seit 1923 steht sie in Fahlenbach in der Hallertau.'],
        ['plitsch', 'Warum „Kunstmühle“? Früher hieß so eine Mühle mit der neuesten Technik: Walzen statt Mühlsteine!'],
        ['kruemel', 'Das Korn kommt von Bauern aus höchstens 40 Kilometern Umkreis. Kurze Wege – das ist unser Trick.'],
      ];
      const [who, text] = steps[this.introStep];
      const last = this.introStep === steps.length - 1;
      this.show(`<div class="kmg-intro">${this.introStep === 3 ? ART.karte(230, false) : SCENE(1, this.season())}${this.bubble(who, text)}<div class="kmg-reihe"><button type="button" class="kmg-btn kmg-btn--gross" data-act="${last ? 'introEnde' : 'introWeiter'}" data-focus>${last ? 'Ich bin bereit!' : 'Weiter'}</button><button type="button" class="kmg-btn kmg-btn--leise" data-act="introEnde">Überspringen</button></div></div>`);
    }
    act_introWeiter() {
      this.introStep++;
      this.showIntro();
    }
    act_introEnde() {
      this.s.intro = true;
      this.persist();
      this.startLevel(1);
    }

    act_level(btn) {
      const l = Number(btn.dataset.level) || 1;
      if (!this.s.intro) return this.act_intro();
      this.startLevel(Math.min(l, this.s.level, 5));
    }

    startLevel(l) {
      this.hideBelow();
      const L = LEVELS[l];
      this.hud.hidden = true;
      this.show(`<div class="kmg-levelstart">${SCENE(l, this.season())}<p class="kmg-jahr">${esc(L.jahr)}</p><h2 class="kmg-titel">${esc(L.titel)}</h2>${this.bubble('kruemel', L.text)}<p class="kmg-neu">${esc(L.neu)}</p><button type="button" class="kmg-btn kmg-btn--gross" data-act="tagStart" data-level="${l}" data-focus>Mühle aufsperren</button></div>`);
    }

    /* ----- Tagesablauf ----- */
    act_tagStart(btn) {
      const l = Number(btn.dataset.level) || 1;
      const L = LEVELS[l];
      const pool = (this.cfg.orders || []).filter((o) => o.level <= l && L.grains.includes(o.grain) && GRAINS[o.grain].sieve.includes(o.flour));
      if (!pool.length) return;
      const M = MODES[this.mode()];
      const n = Math.max(2, Math.max(2, Number(this.cfg.ordersBase) || 4) + (l - 1) + M.orders);
      const fresh = shuffle(pool.filter((o) => o.level === l));
      let list = fresh.slice(0, 2);
      while (list.length < n) list.push(pick(pool));
      list = shuffle(list);
      if (this.season() === 'advent') {
        const p = pool.find((o) => o.pic === 'plaetzchen');
        if (p) list[0] = p;
      }
      const daily = this.s.daily !== today();
      this.day = {
        level: l,
        t: 0,
        len: Math.round(Math.max(90, Number(this.cfg.dayLength) || 240) * (1 + 0.15 * (l - 1)) * (M.time || 1)),
        untimed: !M.time,
        event: null,
        energie: 5,
        eTimer: 0,
        orders: list.map((o, i) => ({ id: i, o, cust: pick(L.kunden), done: false, shown: i < 2, daily: daily && i === 0 })),
        silo: Object.fromEntries(L.grains.map((g) => [g, 0])),
        deliveries: [],
        nextDelivery: 1,
        nextOrder: 0.5 * Math.max(90, Number(this.cfg.dayLength) || 240) / (list.length + 2),
        reste: 0,
        resteSent: 0,
        points: 0,
        foreignDone: l < 2,
        foreignTaken: false,
        baked: [],
        deliveriesTotal: 0,
        hintTick: 0,
      };
      // Zufallsereignis ab Level 2: Gewitter, wenig Wasser in der Ilm oder eine Großbestellung
      const chance = { klein: 0.35, normal: 0.55, meister: 0.8 }[this.mode()];
      if (l >= 2 && Math.random() < chance) {
        const events = ['gewitter', 'niedrigwasser'];
        if (L.packs.includes('bigbag') && this.day.orders.length > 2) events.push('gross');
        this.day.event = pick(events);
        if (this.day.event === 'gross') {
          const o = this.day.orders[1 + Math.floor(Math.random() * (this.day.orders.length - 1))];
          o.cust = 'baecker';
          o.gross = true;
        }
        this.eventNews = true;
      }
      this.paused = false;
      this.hud.hidden = false;
      this.renderHud(true);
      this.renderHof();
      this.root.scrollIntoView({ block: 'nearest', behavior: reduced() ? 'auto' : 'smooth' });
      this.timer = setInterval(() => this.tick(), 250);
      this.lastTick = performance.now();
      this.track('tag_start', { level: l, ereignis: this.day.event || '' });
    }

    hearts(farmer) {
      this.s.herzen = this.s.herzen || {};
      const h = this.s.herzen[farmer.name];
      return h === undefined ? 1 : h;
    }
    addHeart(farmer, diff) {
      const h = Math.max(0, Math.min(5, this.hearts(farmer) + diff));
      this.s.herzen[farmer.name] = h;
      this.persist();
      return h;
    }
    // Bauern mit mehr Herzen kommen öfter vorbei
    pickFarmer(grain) {
      const list = FARMERS.filter((f) => f.grains.includes(grain));
      if (!list.length) return FARMERS[0];
      const weights = list.map((f) => 1 + this.hearts(f));
      let r = Math.random() * weights.reduce((a, b) => a + b, 0);
      for (let i = 0; i < list.length; i++) {
        r -= weights[i];
        if (r <= 0) return list[i];
      }
      return list[0];
    }

    storm() {
      const d = this.day;
      return !!d && d.event === 'gewitter' && d.deliveriesTotal >= 1 && d.deliveriesTotal <= 3;
    }

    stopDay() {
      clearInterval(this.timer);
      this.timer = null;
    }

    setPause(p) {
      if (!this.day || this.day.over) return;
      this.paused = p;
      if (p) {
        this.panel(`<h3 class="kmg-h">Pause</h3>${this.bubble('plitsch', 'Kurze Brotzeit! Die Mühle wartet auf dich.')}<div class="kmg-reihe"><button type="button" class="kmg-btn kmg-btn--gross" data-act="weiter" data-focus>Weiter mahlen</button><button type="button" class="kmg-btn kmg-btn--leise" data-act="hilfe">So geht’s</button><button type="button" class="kmg-btn kmg-btn--leise" data-act="menu">Zum Menü</button></div>`, { cls: 'is-klein', hold: true });
      }
      this.renderHud();
    }
    act_pause() {
      this.setPause(true);
    }
    act_weiter() {
      this.paused = false;
      this.lastTick = performance.now();
      this.closePanel();
    }

    tick() {
      const d = this.day;
      const now = performance.now();
      const dt = (now - this.lastTick) / 1000;
      this.lastTick = now;
      if (!d || d.over || this.paused) return;
      d.real = (d.real || 0) + dt;
      this.spielSek += dt;
      // Erklär-Fenster halten die Zeit an, Spielschritte nicht
      if (!this.holding) d.t += dt;
      const L = LEVELS[d.level];
      const ernte = this.season() === 'ernte' ? 0.7 : 1;

      // Strom: Wasserrad lädt alle 6 s eine Einheit, ab „Heute“ hilft das Sonnendach mit
      d.eTimer += this.holding ? 0 : dt * ((d.event === 'niedrigwasser' ? 0.5 : 1) + (L.solar ? 1 : 0));
      if (d.eTimer >= 6) {
        d.eTimer -= 6;
        if (d.energie < 5) d.energie++;
      }
      if (this.mini && this.mini.type === 'strom' && d.energie >= 2) {
        this.mini = null;
        this.grind();
      }

      // Lieferungen: kommen, solange Korn fehlt; ab Level 2 einmal ein Lkw von weit weg
      const open = d.orders.filter((o) => !o.done);
      const need = {};
      open.forEach((o) => (need[o.o.grain] = (need[o.o.grain] || 0) + 1));
      Object.keys(d.silo).forEach((g) => (need[g] = (need[g] || 0) - d.silo[g]));
      d.deliveries.forEach((x) => !x.foreign && (need[x.grain] = (need[x.grain] || 0) - 2));
      const missing = Object.keys(need).filter((g) => need[g] > 0);
      if (d.deliveries.length < 2 && d.t >= d.nextDelivery && (missing.length || Math.random() < 0.15)) {
        // Der Lkw von weit weg kommt nach der zweiten Lieferung (oder nach einem Fünftel des Tages)
        if (!d.foreignDone && (d.deliveriesTotal >= 2 || d.t > d.len * 0.2)) {
          d.foreignDone = true;
          d.deliveries.push({ foreign: true, grain: 'weizen' });
        } else {
          const grain = missing.length ? pick(missing) : pick(L.grains);
          const farmer = this.pickFarmer(grain);
          const sturm = this.storm();
          const wetChance = sturm ? 0.8 : Math.max(0.06, 0.24 - 0.04 * this.hearts(farmer)) * MODES[this.mode()].wet;
          const wet = d.deliveriesTotal > 0 && Math.random() < wetChance;
          d.deliveries.push({ farmer, grain, wet });
          d.deliveriesTotal++;
        }
        d.nextDelivery = d.t + (missing.length ? 6 : 14) * ernte;
        this.play('tap');
        this.renderHof();
      }

      // Neue Bestellungen: höchstens drei offen sichtbar
      const visible = d.orders.filter((o) => o.shown && !o.done).length;
      const next = d.orders.find((o) => !o.shown);
      // Ist keine Bestellung mehr offen, kommt die nächste sofort – niemand soll warten
      if (next && (visible === 0 || (visible < 3 && d.t >= d.nextOrder))) {
        next.shown = true;
        d.nextOrder = d.t + d.len / (d.orders.length + 2);
        this.renderHof();
      }

      if (d.t >= d.len && !d.untimed) d.timeUp = true;
      // Feierabend erst, wenn kein Arbeitsschritt mehr offen ist
      if (d.timeUp && this.panelEl.hidden) return this.endDay();
      this.renderHud();
    }

    renderHud(rebuild = false) {
      const d = this.day;
      if (!d) return;
      const done = d.orders.filter((o) => o.done).length;
      const p = d.untimed ? done / d.orders.length : Math.min(1, d.t / d.len);
      const left = Math.max(0, Math.ceil(d.len - d.t));
      const en = this.stage.querySelector('[data-energie]');
      if (en && en.dataset.wert !== String(d.energie)) {
        en.dataset.wert = d.energie;
        en.setAttribute('aria-label', `Strom: ${d.energie} von 5`);
        en.innerHTML = `${ART.blitz()}<span class="kmg-energie__wort">Strom</span>${[1, 2, 3, 4, 5].map((i) => `<i class="${i <= d.energie ? 'is-voll' : ''}"></i>`).join('')}`;
      }
      const sun = this.stage.querySelector('.kmg-szene .sonne');
      if (sun) sun.setAttribute('transform', `translate(${40 + p * 560} ${90 - Math.sin(p * Math.PI) * 60})`);
      if (rebuild || !this.hud.querySelector('[data-hud-zeit]')) {
        this.hud.innerHTML = `<span class="kmg-hud__jahr">${esc(LEVELS[d.level].jahr)}</span>
        <span class="kmg-hud__zeit" data-hud-zeit><span class="kmg-hud__bahn"><span data-hud-bahn></span></span><span data-hud-uhr></span></span>
        <span class="kmg-hud__zahl" data-hud-zahl>${ART.bake('semmel')}<span></span></span>
        <button type="button" class="kmg-hud__knopf" data-act="ton" aria-pressed="${this.s.sound}" aria-label="Ton">${svg('0 0 24 24', `<path class="fn" d="M4 9h4l5-4v14l-5-4H4z"/>${this.s.sound ? '<path class="o n" d="M16 9q2 3 0 6M18.5 7q4 5 0 10"/>' : '<path class="o n" d="M16 9l5 6M21 9l-5 6"/>'}`)}</button>
        <button type="button" class="kmg-hud__knopf" data-act="pause" aria-label="Pause">${svg('0 0 24 24', '<path class="fn" d="M6 5h4v14H6zM14 5h4v14h-4z"/>')}</button>`;
      }
      this.hud.querySelector('[data-hud-bahn]').style.width = `${(d.untimed ? p : 1 - p) * 100}%`;
      const uhr = this.hud.querySelector('[data-hud-uhr]');
      uhr.textContent = d.untimed ? 'ohne Uhr' : `${Math.floor(left / 60)}:${String(left % 60).padStart(2, '0')}`;
      this.hud.querySelector('[data-hud-zeit]').setAttribute('aria-label', d.untimed ? 'Ohne Zeitdruck' : `Noch ${left} Sekunden bis Feierabend`);
      const zahl = this.hud.querySelector('[data-hud-zahl]');
      zahl.lastElementChild.textContent = `${done}/${d.orders.length}`;
      zahl.setAttribute('aria-label', `${done} von ${d.orders.length} Bestellungen fertig`);
    }

    // Der Hof: Annahme, Silos, Reste-Anhänger, Bestellungen und ein Tipp von Krümel oder Plitsch
    renderHof() {
      const d = this.day;
      if (!d || d.over || !this.panelEl.hidden) return;
      const L = LEVELS[d.level];
      const del = d.deliveries[0];
      const annahme = del
        ? `<button type="button" class="kmg-karte kmg-karte--ruf" data-act="annahme" aria-label="Lieferung annehmen: ${del.foreign ? 'Lkw von weit weg' : esc(del.farmer.name)}">${ART.traktor(del.foreign)}<span class="kmg-karte__titel">${del.foreign ? 'Ein Lkw!' : esc(del.farmer.name)}</span>${del.foreign ? '' : ART.herzen(this.hearts(del.farmer))}<span class="kmg-karte__info">${d.deliveries.length > 1 ? 'Noch einer wartet' : 'Tipp zum Annehmen'}</span></button>`
        : `<div class="kmg-karte is-leer">${svg('0 0 120 64', '<path class="fl o" d="M10 50h100" />', 'kmg-fahrzeug')}<span class="kmg-karte__titel">Annahme</span><span class="kmg-karte__info">Gleich kommt ein Bauer</span></div>`;
      const silos = Object.keys(d.silo)
        .map((g) => `<li class="kmg-silo${d.silo[g] ? '' : ' is-leer'}">${ART.grain(g)}<span>${esc(GRAINS[g].name)}</span><b aria-label="${d.silo[g]} Portionen">${d.silo[g]}</b></li>`)
        .join('');
      const orders = d.orders
        .filter((o) => o.shown && !o.done)
        .map((o) => {
          const c = CUSTOMERS[o.cust];
          return `<li><button type="button" class="kmg-bestellung${o.daily ? ' is-tag' : ''}${o.gross ? ' is-gross' : ''}" data-act="bestellung" data-id="${o.id}">${ART.bake(o.o.pic)}<span class="kmg-bestellung__name">${esc(o.o.name)}</span><span class="kmg-bestellung__kunde">${ART.kunde(o.cust)}${o.gross ? 'Großbestellung' : esc(c.wo)}${o.daily ? ' · Tagesauftrag' : ''}</span></button></li>`;
        })
        .join('');
      const anh = d.reste >= 3;
      d.resteHof = d.reste;
      this.stage.innerHTML = `<div class="kmg-hof">
        <div class="kmg-szene-wrap${this.season() === 'ernte' ? ' is-ernte' : ''}${this.storm() ? ' is-gewitter' : ''}">${SCENE(d.level, this.season(), true)}${this.badge()}<span class="kmg-energie" data-energie></span></div>
        <div class="kmg-hof__raster">
          <section class="kmg-feld kmg-feld--annahme" aria-label="Getreideannahme">${annahme}</section>
          <section class="kmg-feld" aria-label="Silos"><h3 class="kmg-feld__titel">Silos</h3><ul class="kmg-silos" role="list">${silos}</ul></section>
          <section class="kmg-feld" aria-label="Reste"><h3 class="kmg-feld__titel">Reste</h3><button type="button" class="kmg-anhaenger" data-act="reste" ${anh ? '' : 'disabled'}>${svg('0 0 60 40', '<g class="o"><path class="fh" d="M6 10h40v20H6z"/><circle class="fn" cx="16" cy="32" r="5"/><circle class="fn" cx="36" cy="32" r="5"/><path class="n" d="M46 24h10"/></g><path class="fs" d="M8 10q18-10 36 0z"/>', 'kmg-ico')}<span>${d.reste} ${L.solar ? 'Kleie und Spelzen' : 'Kleie'}</span><small>${anh ? 'Ab zu den Tieren!' : 'ab 3 geht’s los'}</small></button></section>
        </div>
        <section class="kmg-feld kmg-feld--bestellungen" aria-label="Bestellungen"><h3 class="kmg-feld__titel">Bestellungen</h3>${orders ? `<ul class="kmg-bestellungen" role="list">${orders}</ul>` : '<p class="kmg-leise">Gerade keine Bestellung – gleich kommt die nächste.</p>'}</section>
        ${this.hint()}
      </div>`;
      this.renderHud();
      this.pointHof();
    }

    badge() {
      const d = this.day;
      const season = this.season();
      const ev = { gewitter: this.storm() ? 'Gewitter!' : '', niedrigwasser: 'Wenig Wasser in der Ilm', gross: 'Großbestellung' }[d.event] || '';
      const text = ev || (season === 'ernte' ? 'Erntezeit!' : season === 'advent' ? 'Weihnachtsbäckerei' : '');
      return text ? `<span class="kmg-saison">${text}</span>` : '';
    }

    /* ----- Zeigefinger: beim ersten Spiel und als „Kleiner Müller“ zeigt er auf den nächsten Schritt ----- */
    fingerOn() {
      return !this.s.tutorial || this.mode() === 'klein';
    }
    point(el) {
      this.root.querySelectorAll('.kmg-finger').forEach((f) => f.remove());
      if (!el || !this.fingerOn()) return;
      el.classList.add('has-finger');
      el.insertAdjacentHTML(
        'beforeend',
        `<span class="kmg-finger" aria-hidden="true">${svg('0 0 40 48', '<g class="o"><path class="fl" d="M14 4c3 0 4 2 4 5v13l2-1c2-1 4 0 5 2l1 1c2-1 4 0 5 2l1 1c2-1 5 0 5 3v8c0 6-5 10-11 10h-6c-4 0-7-2-9-5L4 31c-2-2-1-5 2-5 2 0 3 1 4 2l1 1V9c0-3 1-5 3-5z"/></g>')}</span>`
      );
    }
    pointHof() {
      const d = this.day;
      if (!d || !this.fingerOn()) return;
      const q = (sel) => this.stage.querySelector(sel);
      let target = null;
      if (d.reste >= 3) target = q('[data-act="reste"]');
      else {
        const ready = d.orders.find((o) => o.shown && !o.done && this.answers(o.o).some((a) => d.silo[a.grain] > 0));
        if (ready) target = q(`[data-act="bestellung"][data-id="${ready.id}"]`);
        else target = q('[data-act="annahme"]');
      }
      this.point(target);
    }

    hint() {
      const d = this.day;
      if (this.nextFact) {
        const f = this.nextFact;
        this.nextFact = null;
        return this.bubble('plitsch', `Wusstest du? ${f.q} – ${f.a}`);
      }
      if (this.eventNews && d.event) {
        this.eventNews = false;
        const news = {
          gewitter: 'Heute zieht ein Gewitter auf! Dann bringen die Bauern oft nasses Korn – zieh genau Proben.',
          niedrigwasser: LEVELS[d.level].solar
            ? 'Heute führt die Ilm wenig Wasser. Zum Glück hilft das Sonnendach beim Strom!'
            : 'Heute führt die Ilm wenig Wasser. Das Wasserrad lädt nur halb so schnell Strom.',
          gross: 'Die Bäckerei hat eine Großbestellung! Dafür brauchst du einen BigBag.',
        };
        return this.bubble('plitsch', news[d.event], 'oh');
      }
      if (this.storm() && !d.stormSaid) {
        d.stormSaid = true;
        return this.bubble('plitsch', 'Es donnert! Jetzt ist das Korn oft nass. Zieh genau Proben.', 'oh');
      }
      if (d.timeUp) return this.bubble('kruemel', 'Die Sonne geht unter. Mach noch fertig, dann ist Feierabend!');
      const silo = Object.values(d.silo).reduce((a, b) => a + b, 0);
      if (d.reste >= 3) return this.bubble('plitsch', 'Der Anhänger ist voll! Bring die Reste zu den Tieren auf dem Bauernhof.');
      if (d.energie < 2) return this.bubble('plitsch', 'Der Strom ist fast leer. Das Wasserrad lädt nach – nimm solange Korn an!');
      if (d.deliveries.length && !silo) return this.bubble('kruemel', 'Ein Bauer ist da! Tipp auf die Annahme und zieh eine Probe.');
      if (silo) return this.bubble('kruemel', 'Korn ist im Silo. Tipp auf eine Bestellung und mahle das Korn zum passenden Mehl.');
      return this.bubble('plitsch', 'Warten wir auf den nächsten Traktor. Hörst du ihn schon tuckern?');
    }

    /* ----- Lieferung annehmen: Probe ziehen ----- */
    act_annahme() {
      const d = this.day;
      const del = d.deliveries[0];
      if (!del) return;
      if (del.foreign) {
        return this.panel(
          `<h3 class="kmg-h">Ein Lkw von ganz weit weg</h3>${ART.karte(0, true)}${this.bubble('plitsch', 'Der Fahrer ruft: „Weizen aus 900 Kilometern Entfernung – superbillig!“ Was machst du?', 'oh')}<div class="kmg-reihe"><button type="button" class="kmg-btn" data-act="fremd" data-ja="1">Abladen</button><button type="button" class="kmg-btn kmg-btn--gross" data-act="fremd" data-focus>Nein danke!</button></div>`
        );
      }
      del.value = del.wet ? 16 + Math.round(Math.random() * 3) : 12 + Math.round(Math.random() * 2);
      this.panel(
        `<h3 class="kmg-h">${esc(del.farmer.name)} bringt ${esc(GRAINS[del.grain].name)}</h3>
        <div class="kmg-zwei">${ART.karte(del.farmer.ang, false)}<p class="kmg-leise">${esc(del.farmer.kreis)}<br><b>${del.farmer.km} km</b> bis zur Mühle<br>${ART.herzen(this.hearts(del.farmer))}</p></div>
        ${this.bubble('kruemel', 'Zieh eine Probe: Ist das Korn trocken genug?')}
        <div class="kmg-probe" data-probe>${ART.feuchte()}<p class="kmg-probe__wert" data-wert>? %</p></div>
        <div class="kmg-reihe" data-wahl><button type="button" class="kmg-btn kmg-btn--gross" data-act="probe" data-focus>Probe ziehen</button></div>`
      );
    }
    async act_probe(btn) {
      const del = this.day.deliveries[0];
      btn.disabled = true;
      const box = this.panelInner.querySelector('[data-probe]');
      const needle = box.querySelector('.nadel');
      const deg = -80 + ((del.value - 10) / 10) * 160;
      needle.style.transform = `rotate(${Math.max(-80, Math.min(80, deg))}deg)`;
      await wait(900);
      box.querySelector('[data-wert]').textContent = `${del.value} % Feuchte`;
      this.panelInner.querySelector('[data-wahl]').innerHTML = `<button type="button" class="kmg-btn kmg-btn--gross" data-act="abladen" data-focus>Abladen</button><button type="button" class="kmg-btn" data-act="zurueck">Zurück zum Hof</button>`;
      this.panelInner.querySelector('[data-act="abladen"]').focus();
    }
    act_abladen() {
      const d = this.day;
      const del = d.deliveries.shift();
      if (del.wet) {
        this.play('schade');
        return this.panel(`<h3 class="kmg-h">Oh je, zu nass!</h3>${this.bubble('plitsch', `${del.value} % Feuchte – nasses Korn wird im Silo muffig. Über 14,5 % darf es nicht hinein. Das muss leider zurück.`, 'sad')}<button type="button" class="kmg-btn kmg-btn--gross" data-act="schliessen" data-focus>Verstanden</button>`, { hold: true });
      }
      del.herz = this.addHeart(del.farmer, 1);
      this.cleaning(del);
    }
    act_zurueck() {
      const d = this.day;
      const del = d.deliveries.shift();
      if (del.wet) {
        this.play('gut');
        // Der Bauer trocknet nach und kommt wieder
        d.nextDelivery = Math.min(d.nextDelivery, d.t + 5);
        const h = this.addHeart(del.farmer, 1);
        return this.panel(`<h3 class="kmg-h">Gut aufgepasst!</h3>${ART.herzen(h)}${this.bubble('kruemel', `${del.farmer.name}: „Danke, dass du so gut aufpasst! Ich trockne es noch ein bisschen. Bis später!“ Dafür bekommst du ein Herz.`)}<button type="button" class="kmg-btn kmg-btn--gross" data-act="schliessen" data-focus>Weiter</button>`, { hold: true });
      }
      this.play('schade');
      d.nextDelivery = Math.min(d.nextDelivery, d.t + 4);
      const h = this.addHeart(del.farmer, -1);
      this.panel(`<h3 class="kmg-h">Das war doch trocken</h3>${ART.herzen(h)}${this.bubble('plitsch', `${del.value} % Feuchte ist trocken genug – bis 14,5 % darf das Korn ins Silo. ${del.farmer.name} ist ein bisschen traurig und kommt gleich nochmal.`, 'oh')}<button type="button" class="kmg-btn kmg-btn--gross" data-act="schliessen" data-focus>Weiter</button>`, { hold: true });
    }
    act_fremd(btn) {
      const d = this.day;
      d.deliveries.shift();
      if (btn.dataset.ja) {
        d.foreignTaken = true;
        d.silo.weizen = (d.silo.weizen || 0) + 2;
        this.play('schade');
        return this.panel(`<h3 class="kmg-h">Hmm …</h3>${this.bubble('kruemel', 'Das Korn ist 900 Kilometer gefahren. Unsere Bauern sind gleich ums Eck – das spart Wege und Sprit. Kein Regional-Stern heute.', 'sad')}<button type="button" class="kmg-btn kmg-btn--gross" data-act="schliessen" data-focus>Nächstes Mal!</button>`, { hold: true });
      }
      this.play('stern');
      this.panel(`<h3 class="kmg-h">Regional-Stern!</h3>${ART.stern()}${this.bubble('kruemel', 'Richtig! Unser Korn wächst höchstens 40 Kilometer von der Mühle entfernt. So bleiben die Wege kurz.')}<button type="button" class="kmg-btn kmg-btn--gross" data-act="schliessen" data-focus>Weiter</button>`, { hold: true });
    }
    act_schliessen() {
      this.mini = null;
      if (this.day && this.day.timeUp) return this.endDay();
      this.closePanel();
    }

    /* ----- Reinigen: Steinchen, Stroh und dunkle Körner raustippen ----- */
    cleaning(del) {
      const d = this.day;
      const L = LEVELS[d.level];
      const items = [];
      for (let i = 0; i < 12; i++) items.push('korn');
      ['stein', 'stein', 'stroh', 'stroh', 'dunkel', 'dunkel'].forEach((x) => items.push(x));
      const list = shuffle(items);
      const pics = {
        korn: `<ellipse class="${{ weizen: 'fg', roggen: 'fe', rotkorn: 'fz', dinkel: 'fs' }[del.grain]} ol" cx="12" cy="12" rx="6" ry="9" transform="rotate(30 12 12)"/>`,
        stein: '<path class="fl ol" d="M4 16q-2-8 6-11 8-2 11 4 2 9-6 11-9 1-11-4z" opacity=".9"/><path class="fh" d="M6 15q4 3 10 1" opacity=".5"/>',
        stroh: '<path class="fs ol" d="M3 18L19 4l3 2L6 20z"/>',
        dunkel: '<ellipse class="fn ol" cx="12" cy="12" rx="6" ry="9" transform="rotate(-30 12 12)"/>',
      };
      const html = list
        .map((x, i) => {
          const lane = i % 3;
          const delay = (i * 0.45).toFixed(2);
          return `<button type="button" class="kmg-teil" data-act="clean" data-art="${x}" style="--bahn:${lane};--start:${delay}s;--dauer:${(4.6 + (i % 4) * 0.4).toFixed(1)}s" aria-label="${x === 'korn' ? 'gutes Korn' : x === 'stein' ? 'Steinchen' : x === 'stroh' ? 'Strohhalm' : 'dunkles Korn'}">${svg('0 0 24 24', pics[x])}</button>`;
        })
        .join('');
      this.mini = { type: 'clean', del, bad: 6, got: 0, oops: 0 };
      this.panel(
        `<h3 class="kmg-h">Reinigen</h3>${this.bubble('plitsch', L.kamera ? 'Die Kamera-Maschine pustet dunkle Körner weg. Tipp du schnell Steinchen und Stroh raus!' : 'Tipp schnell alle Steinchen, Strohhalme und dunklen Körner raus!')}<div class="kmg-band${L.kamera ? ' has-kamera' : ''}${reduced() ? ' is-ruhig' : ''}" data-band>${L.kamera ? `<span class="kmg-kamera">${svg('0 0 40 30', '<g class="o"><rect class="fn" x="4" y="8" width="32" height="20" rx="3"/><circle class="fb" cx="20" cy="18" r="6"/><path class="fn" d="M14 8l3-5h6l3 5z"/></g>')}</span>` : ''}${html}</div><p class="kmg-leise" data-clean-info>Gefunden: 0 von 6</p>`,
        { cls: 'is-mini' }
      );
      const band = this.panelInner.querySelector('[data-band]');
      if (L.kamera) {
        this.kameraTimer = setTimeout(() => {
          band.querySelectorAll('[data-art="dunkel"]').forEach((el) => this.removeBad(el, true));
        }, 1600);
      }
      this.miniTimer = setTimeout(() => this.cleaningDone(), (reduced() ? 12000 : 10500) + MODES[this.mode()].clean * 1000);
    }
    removeBad(el, auto) {
      if (!this.mini || this.mini.type !== 'clean' || el.classList.contains('is-weg')) return;
      el.classList.add('is-weg', auto ? 'is-kamera' : 'is-puff');
      el.disabled = true;
      this.mini.got++;
      const info = this.panelInner.querySelector('[data-clean-info]');
      if (info) info.textContent = `Gefunden: ${this.mini.got} von ${this.mini.bad}`;
      if (this.mini.got >= this.mini.bad) {
        clearTimeout(this.miniTimer);
        setTimeout(() => this.cleaningDone(), 500);
      }
    }
    act_clean(btn) {
      if (!this.mini || this.mini.type !== 'clean') return;
      if (btn.dataset.art === 'korn') {
        this.mini.oops++;
        btn.classList.add('is-wackel');
        setTimeout(() => btn.classList.remove('is-wackel'), 400);
        return;
      }
      this.play('tap');
      this.removeBad(btn, false);
    }
    cleaningDone() {
      if (!this.mini || this.mini.type !== 'clean' || !this.day) return;
      const { del, got, bad } = this.mini;
      this.mini = null;
      const d = this.day;
      const bonus = (del.herz || 0) >= 4;
      d.silo[del.grain] = (d.silo[del.grain] || 0) + (bonus ? 3 : 2);
      this.play('gut');
      const menge = bonus ? 'drei' : 'zwei';
      let msg =
        got >= bad
          ? `Blitzblank! ${GRAINS[del.grain].name} für ${menge} Bestellungen ist jetzt im Silo.`
          : `Ein paar Teile sind durchgerutscht – macht nichts, das Sieb fängt sie auf. ${GRAINS[del.grain].name} für ${menge} Bestellungen ist im Silo.`;
      msg += bonus ? ` ${del.farmer.name} mag dich so gern: Es gibt eine Extra-Portion!` : ` ${del.farmer.name} freut sich: ein Herz mehr!`;
      if (del.farmer.witz) msg += ` Und: „${del.farmer.witz}“`;
      this.panel(`<h3 class="kmg-h">Ab ins Silo</h3>${this.bubble('kruemel', msg)}<button type="button" class="kmg-btn kmg-btn--gross" data-act="schliessen" data-focus>Weiter</button>`, { hold: true });
    }

    /* ----- Bestellung: Korn wählen → (schälen) → mahlen → sieben → abfüllen → backen ----- */
    act_bestellung(btn) {
      const d = this.day;
      const ord = d.orders.find((o) => o.id === Number(btn.dataset.id));
      if (!ord) return;
      this.job = { ord, step: 0 };
      const c = CUSTOMERS[ord.cust];
      const grains = Object.keys(d.silo)
        .map((g) => `<button type="button" class="kmg-wahl" data-act="korn" data-grain="${g}" ${d.silo[g] ? '' : 'disabled'}>${ART.grain(g)}<span>${esc(GRAINS[g].name)}</span><small>${d.silo[g] ? `${d.silo[g]} im Silo` : 'Silo leer'}</small></button>`)
        .join('');
      const empty = !Object.values(d.silo).some(Boolean);
      this.panel(
        `<h3 class="kmg-h">${esc(c.name)} möchte: ${esc(ord.o.name)}</h3><div class="kmg-auftrag">${ART.bake(ord.o.pic)}</div>
        ${this.bubble('kruemel', empty ? 'Die Silos sind leer! Nimm zuerst Korn von einem Bauern an.' : 'Welches Korn nimmst du dafür?', empty ? 'oh' : 'happy')}
        <div class="kmg-wahlen">${grains}</div>
        <div class="kmg-reihe"><button type="button" class="kmg-btn kmg-btn--leise" data-act="tipp">Krümel fragen</button><button type="button" class="kmg-btn kmg-btn--leise" data-act="schliessen">Später</button></div>`
      );
      if (this.mode() === 'klein' && !empty) this.act_tipp(this.panelInner.querySelector('[data-act="tipp"]'));
    }
    // Alle richtigen Antworten einer Bestellung: Haupt-Mehl plus „Auch richtig“ aus den Blöcken
    answers(o) {
      const L = LEVELS[this.day ? this.day.level : 5];
      return [{ grain: o.grain, flour: o.flour }, ...(o.alts || [])].filter(
        (a, i) => GRAINS[a.grain] && GRAINS[a.grain].sieve.includes(a.flour) && (i === 0 || L.grains.includes(a.grain))
      );
    }
    act_tipp(btn) {
      const o = this.job.ord.o;
      const grains = [...new Set(this.answers(o).map((a) => GRAINS[a.grain].name))];
      const bubble = btn.closest('.kmg-panel__inner').querySelector('.kmg-sprech');
      bubble.outerHTML = this.bubble('kruemel', `${o.tip ? `${o.tip}. ` : ''}Dafür passt ${grains.join(' oder ')}.`);
    }
    act_korn(btn) {
      const d = this.day;
      const g = btn.dataset.grain;
      if (!d.silo[g]) return;
      d.silo[g]--;
      this.job.grain = g;
      this.job.flourIdx = 0;
      if (g === 'dinkel') return this.peel();
      this.grind();
    }

    // Zu wenig Strom: kurz warten, bis das Wasserrad nachgeladen hat
    waitPower() {
      this.mini = { type: 'strom' };
      const L = LEVELS[this.day.level];
      this.panel(
        `<h3 class="kmg-h">Kurz Strom tanken</h3><div class="kmg-strom-warten">${svg('0 0 80 80', `<g transform="translate(40 40)"><g class="rad"><circle class="fh o" r="30"/><circle class="fl o" r="22"/><path class="o n" d="M0-30V30M-30 0H30M-21-21l42 42M21-21l-42 42"/><circle class="fn" r="5"/></g></g>`)}</div>${this.bubble('plitsch', L.solar ? 'Der Walzenstuhl braucht Strom. Ich drehe das Wasserrad, und die Sonne hilft auf dem Dach mit – gleich geht’s weiter!' : 'Der Walzenstuhl braucht Strom. Ich drehe das Wasserrad so schnell ich kann – gleich geht’s weiter!')}`,
        { cls: 'is-mini' }
      );
    }

    peel() {
      this.mini = { type: 'peel' };
      this.panel(
        `<h3 class="kmg-h">Dinkel schälen</h3>${this.bubble('plitsch', 'Dinkel steckt in einem festen Mantel, dem Spelz. Halte den Knopf gedrückt, bis er ab ist!')}<div class="kmg-schaeler">${ART.grain('dinkel')}<span class="kmg-spelz" data-spelz></span></div><button type="button" class="kmg-btn kmg-btn--gross kmg-halten" data-hold data-focus><span class="kmg-halten__balken" data-hold-bar></span><span>Gedrückt halten</span></button>`,
        { cls: 'is-mini' }
      );
      const b = this.panelInner.querySelector('[data-hold]');
      const bar = this.panelInner.querySelector('[data-hold-bar]');
      let p = 0;
      let iv = null;
      const stop = () => {
        clearInterval(iv);
        iv = null;
      };
      const go = (e) => {
        if (e) e.preventDefault();
        if (iv) return;
        iv = setInterval(() => {
          p = Math.min(1, p + 0.06);
          bar.style.width = `${p * 100}%`;
          this.panelInner.querySelector('[data-spelz]').style.opacity = String(1 - p);
          if (p >= 1) {
            stop();
            this.mini = null;
            this.day.reste += 1;
            this.play('gut');
            setTimeout(() => this.grind(), 300);
          }
        }, 60);
      };
      b.addEventListener('pointerdown', go);
      ['pointerup', 'pointerleave', 'pointercancel'].forEach((ev) => b.addEventListener(ev, stop));
      b.addEventListener('keydown', (e) => (e.key === ' ' || e.key === 'Enter') && go(e));
      b.addEventListener('keyup', stop);
      b.addEventListener('contextmenu', (e) => e.preventDefault());
    }

    grind() {
      const d = this.day;
      if (d.energie < 2) return this.waitPower();
      d.energie -= 2;
      this.renderHud();
      this.job.taps = 0;
      this.mini = { type: 'grind' };
      this.panel(
        `<h3 class="kmg-h">Mahlen</h3>${this.bubble('kruemel', 'Die Walzen zerkleinern das Korn ganz sanft. Tipp fünfmal auf den Walzenstuhl!')}<button type="button" class="kmg-walzenstuhl" data-act="walze" aria-label="Walzenstuhl drehen" data-focus>${ART.walzen()}<span class="kmg-haufen ${FLOURS.vollkorn.tone}" data-haufen style="--h:0"></span></button><p class="kmg-leise" data-mahl-info>0 von 5</p>`,
        { cls: 'is-mini' }
      );
    }
    act_walze(btn) {
      const j = this.job;
      if (!j || j.taps >= 5) return;
      j.taps++;
      this.play('mahl');
      btn.classList.remove('is-dreh');
      void btn.offsetWidth;
      btn.classList.add('is-dreh');
      btn.querySelector('[data-haufen]').style.setProperty('--h', j.taps / 5);
      this.panelInner.querySelector('[data-mahl-info]').textContent = `${j.taps} von 5`;
      if (j.taps >= 5) {
        this.mini = null;
        setTimeout(() => this.sieve(), 450);
      }
    }

    sieve() {
      const j = this.job;
      const o = j.ord.o;
      const seq = GRAINS[j.grain].sieve;
      const f = FLOURS[seq[j.flourIdx]];
      const can = j.flourIdx < seq.length - 1;
      const skala = seq
        .map((t, i) => {
          const x = FLOURS[t];
          const hilfe = this.mode() !== 'meister';
          return `<li class="kmg-skala__stufe${i === j.flourIdx ? ' is-jetzt' : ''}"><span class="kmg-skala__farbe ${x.tone}"></span><b>${t === 'vollkorn' ? 'Vollkorn' : esc(t)}</b>${hilfe ? `<small>${esc(x.hint)}</small><small>${i === 0 ? 'ungesiebt' : `${i}× sieben`}</small>` : ''}</li>`;
        })
        .join('');
      const say =
        j.grain === 'rotkorn'
          ? 'Rotkorn gibt es nur als Vollkorn. Hier musst du nicht sieben.'
          : 'Jedes Sieben hält Schale zurück: Das Mehl wird heller. Siebe, bis es zur Bestellung passt!';
      this.panel(
        `<h3 class="kmg-h">Sieben</h3><p class="kmg-auftragzeile">${ART.bake(o.pic)}<span><b>${esc(o.name)}</b>${o.tip && this.mode() !== 'meister' ? `<br>Merke: ${esc(o.tip)}` : ''}</span></p>${this.bubble('plitsch', say)}
        <div class="kmg-sieb${j.shake ? ' is-schuettel' : ''}"><span class="kmg-sieb__rahmen"></span><span class="kmg-haufen ${f.tone}" style="--h:.5"></span></div>
        <ol class="kmg-skala" aria-label="Mehlskala von dunkel nach hell">${skala}</ol>
        <div class="kmg-reihe"><button type="button" class="kmg-btn" data-act="sieben" ${can ? '' : 'disabled'}>${can ? 'Nochmal sieben' : 'Heller geht’s nicht'}</button><button type="button" class="kmg-btn kmg-btn--gross" data-act="mehlFertig" data-focus>Fertig, abfüllen</button></div>`,
        { cls: 'is-mini' }
      );
      j.shake = false;
    }
    act_sieben() {
      const j = this.job;
      if (j.flourIdx >= GRAINS[j.grain].sieve.length - 1) return;
      j.flourIdx++;
      j.shake = true;
      this.day.reste += 1;
      this.play('mahl');
      this.sieve();
    }
    act_mehlFertig() {
      const j = this.job;
      j.flour = GRAINS[j.grain].sieve[j.flourIdx];
      const L = LEVELS[this.day.level];
      const c = CUSTOMERS[j.ord.cust];
      const frage = j.ord.gross ? 'Die Bäckerei Maier backt für ein großes Fest und braucht riesig viel Mehl auf einmal!' : c.frage;
      const packs = L.packs.map((p) => `<button type="button" class="kmg-wahl" data-act="packen" data-pack="${p}">${ART.pack(p)}<span>${esc(PACKS[p][0])}</span><small>${esc(PACKS[p][1])}</small></button>`).join('');
      this.panel(
        `<h3 class="kmg-h">Abfüllen</h3><div class="kmg-kunde">${ART.kunde(j.ord.cust)}<p><b>${esc(c.name)}</b><br>${esc(c.wo)}</p></div>${this.bubble('kruemel', `${frage} Worin füllst du das Mehl ab?`)}<div class="kmg-wahlen">${packs}</div>`
      );
    }
    async act_packen(btn) {
      const d = this.day;
      const j = this.job;
      const o = j.ord.o;
      const c = CUSTOMERS[j.ord.cust];
      const ans = this.answers(o);
      const okGrain = ans.some((a) => a.grain === j.grain);
      const match = ans.find((a) => a.grain === j.grain && a.flour === j.flour);
      const okFlour = !!match;
      const okPack = j.ord.gross ? btn.dataset.pack === 'bigbag' : c.packs.includes(btn.dataset.pack);
      const pts = (okGrain ? 1 : 0) + (okFlour ? 1 : 0) + (okPack ? 1 : 0);
      d.points += pts;
      j.ord.done = true;
      if (!this.s.cards.includes(j.grain)) {
        this.s.cards.push(j.grain);
        j.newCard = j.grain;
      }
      if (okFlour) d.baked.push(o);
      if (j.ord.daily && okFlour) {
        this.s.daily = today();
        const ur = CARDS.filter((x) => x.ur && !this.s.cards.includes(x.id))[0];
        if (ur) {
          this.s.cards.push(ur.id);
          j.newCard = ur.id;
        }
      }
      this.persist();
      this.panel(`<h3 class="kmg-h">Ab in den Ofen!</h3><div class="kmg-backen">${ART.ofen()}</div>`, { cls: 'is-mini' });
      this.mini = { type: 'bake' };
      await wait(1400);
      this.mini = null;
      let text;
      const name = (a) => `${GRAINS[a.grain].name}, ${FLOURS[a.flour].name}`;
      const want = ans.map(name).join(' oder ');
      const same = ans.filter((a) => a.grain === j.grain);
      if (okFlour && match === ans[0]) text = `Lecker! ${o.name} ist perfekt geworden.${o.tip ? ` ${o.tip}.` : ''}`;
      else if (okFlour) text = `Lecker! ${o.name} mit ${name(match)} – das klappt auch! Bei uns gibt es sie oft mit ${name(ans[0])}.`;
      else if (!okGrain) text = `Plumps! Für ${o.name} passt ${[...new Set(ans.map((a) => GRAINS[a.grain].name))].join(' oder ')}. Richtig wäre: ${want}.`;
      else {
        const seq = GRAINS[j.grain].sieve;
        const dunkel = same.every((a) => seq.indexOf(j.flour) < seq.indexOf(a.flour));
        text = `Plumps! Das Mehl war zu ${dunkel ? 'dunkel' : 'hell'}. Richtig wäre: ${same.map(name).join(' oder ')}.${o.tip ? ` Merke: ${o.tip}.` : ''}`;
      }
      if (!okPack) text += j.ord.gross ? ' Und: Für eine Großbestellung brauchst du einen BigBag – da passt bis zu eine Tonne Mehl hinein!' : ` Und: ${c.tip}`;
      else if (j.ord.gross) text += ' Ein ganzer BigBag – die Bäckerei ist begeistert!';
      this.play(okFlour ? 'gut' : 'schade');
      const card = j.newCard ? CARDS.find((x) => x.id === j.newCard) : null;
      this.panel(
        `<h3 class="kmg-h">${okFlour ? 'Geschafft!' : 'Hoppla!'}</h3><div class="kmg-auftrag${okFlour ? ' is-gut' : ''}">${ART.bake(o.pic, !okFlour)}</div><p class="kmg-sterne" aria-label="${pts} von 3 Punkten">${[1, 2, 3].map((i) => ART.stern(i <= pts)).join('')}</p>${this.bubble(okFlour ? 'kruemel' : 'plitsch', text, okFlour ? 'happy' : 'oh')}${card ? `<p class="kmg-neukarte">Neue Sammelkarte: <b>${esc(card.name)}</b></p>` : ''}<button type="button" class="kmg-btn kmg-btn--gross" data-act="fertig" data-focus>Weiter</button>`,
        { hold: true }
      );
    }
    act_fertig() {
      const d = this.day;
      this.job = null;
      const facts = (this.cfg.facts || []).filter((f) => f.level <= d.level);
      if (facts.length) {
        this.nextFact = facts[this.s.factIdx % facts.length];
        this.s.factIdx++;
        this.persist();
      }
      if (d.orders.every((o) => o.done) || d.timeUp) return this.endDay();
      this.closePanel();
    }

    act_reste() {
      const d = this.day;
      if (d.reste < 3) return;
      const L = LEVELS[d.level];
      d.resteSent += d.reste;
      d.reste = 0;
      this.play('gut');
      this.panel(
        `<h3 class="kmg-h">Nichts geht verloren</h3>${svg('0 0 120 64', '<g class="o"><path class="fh" d="M10 24h56v24H10z"/><circle class="fn" cx="24" cy="52" r="7"/><circle class="fn" cx="52" cy="52" r="7"/><path class="fz" d="M70 26h26l4 12h12v12H70z"/><circle class="fn" cx="84" cy="52" r="9"/></g><path class="fs" d="M12 24q26-14 52 0z"/>', 'kmg-fahrzeug kmg-faehrt')}${this.bubble('kruemel', L.solar ? 'Die Kleie wird Tierfutter, und aus Dinkelspelzen macht die Mühle seit 2022 Einstreu-Pellets für den Stall.' : 'Die Kleie – das sind die Schalen vom Korn – bekommen die Tiere auf dem Bauernhof als Futter.')}<button type="button" class="kmg-btn kmg-btn--gross" data-act="schliessen" data-focus>Weiter</button>`,
        { hold: true }
      );
    }

    /* ----- Feierabend ----- */
    endDay() {
      const d = this.day;
      if (!d || d.over) return;
      d.over = true;
      this.stopDay();
      this.mini = null;
      clearTimeout(this.miniTimer);
      clearTimeout(this.kameraTimer);
      const n = d.orders.length;
      const kunden = d.points / (3 * n) >= 0.6;
      const regional = !d.foreignTaken;
      // Reste-Stern: Der Anhänger stand beim letzten Blick auf den Hof nicht voll (wer nie wegbringt, hat ihn irgendwann voll).
      // Was die letzte Bestellung noch an Kleie erzeugt, zählt nicht – danach ist ja schon Feierabend.
      const reste = (d.resteHof || 0) < 3;
      const stars = [kunden, regional, reste].filter(Boolean).length;
      const prev = this.s.stars[d.level] || 0;
      this.s.stars[d.level] = Math.max(prev, stars);
      const unlocked = stars >= 1 && d.level === this.s.level && d.level < 5;
      if (unlocked) this.s.level = d.level + 1;
      this.s.tutorial = true;
      this.s.played = (this.s.played || 0) + 1;
      this.persist();
      this.track('tag_ende', {
        level: d.level,
        sterne: stars,
        kunden,
        regional,
        reste,
        quote: Math.round((100 * d.points) / (3 * n)),
        bestellungen: n,
        sek: Math.round(d.real || 0),
        ereignis: d.event || '',
        neu_frei: unlocked,
        hoechstes: this.s.level,
      });
      this.play(stars ? 'stern' : 'schade');

      const crit = [
        [kunden, 'Kunden glücklich', kunden ? 'Fast alle haben das richtige Mehl bekommen.' : 'Ein paar Kunden haben noch gewartet oder das falsche Mehl bekommen.'],
        [regional, 'Korn aus der Region', regional ? 'Alles Korn kam aus höchstens 40 km.' : 'Der Lkw von weit weg hat dir diesen Stern gekostet.'],
        [reste, 'Reste verwertet', reste ? 'Kleie und Spelzen sind bei den Tieren.' : 'Der Reste-Anhänger ist heute zu selten zu den Tieren gefahren.'],
      ]
        .map(([ok, t, x]) => `<li class="${ok ? 'is-ok' : ''}">${ART.stern(ok)}<span><b>${t}</b><br>${x}</span></li>`)
        .join('');

      const seen = new Set();
      const baked = d.baked.filter((o) => o.product && o.product.url && !seen.has(o.product.url) && seen.add(o.product.url));
      const backen = baked.length
        ? `<div class="kmg-bruecke"><h4>${esc(this.cfg.texts?.backen || 'Jetzt in echt backen')}</h4><ul role="list">${baked
            .map(
              (o) =>
                `<li><a href="${esc(o.product.url)}" data-stat="produkt" data-stat-name="${esc(o.name)}">${o.product.image ? `<img src="${esc(o.product.image)}" alt="" width="48" height="48" loading="lazy">` : ART.bake(o.pic)}<span><b>${esc(o.name)}</b><br>${esc(o.product.title)}</span></a>${o.recipe ? `<a class="kmg-rezept" href="${esc(o.recipe)}" data-stat="rezept" data-stat-name="${esc(o.name)}" target="_blank" rel="noopener">Rezept<span class="kmg-vh"> für ${esc(o.name)} (öffnet backdeinbrot.de)</span></a>` : ''}</li>`
            )
            .join('')}</ul></div>`
        : '';
      const mb = this.cfg.malbuch;
      const malbuch =
        mb && mb.variant && mb.available
          ? `<div class="kmg-bruecke kmg-bruecke--malbuch">${mb.image ? `<img src="${esc(mb.image)}" alt="" width="96" height="96" loading="lazy">` : ''}<div><h4>Das Mehlmalbuch zum Ausmalen</h4><p>${esc(this.cfg.texts?.malbuch || '')}</p><button type="button" class="kmg-btn" data-act="malbuch">${this.s.malbuch ? 'Liegt im Warenkorb' : 'Malbuch in den Warenkorb'}</button></div></div>`
          : '';
      const ld = this.cfg.laden;
      const laden =
        ld && ld.url
          ? `<div class="kmg-bruecke kmg-bruecke--laden">${svg('0 0 60 60', '<g class="o"><path class="fm" d="M8 26h44v30H8z"/><path class="fz" d="M4 28l26-20 26 20z"/><path class="fh" d="M24 38h12v18H24z"/><path class="fw" d="M6 26h48l-3 7H9z"/></g><path class="fz" d="M10 26h6l-1 7h-5zM22 26h6v7h-6zM34 26h6v7h-6zM46 26h5l-1 7h-4z"/>', 'kmg-ico')}<div><h4>Besuch die echte Mühle!</h4><p>Im Mühlenladen in Fahlenbach gibt es alle Mehle aus dem Spiel – frisch gemahlen.${this.cfg.texts?.ladenBelohnung ? ` Komm selbst vorbei und zeig uns deine Urkunde – dann wartet ${esc(this.cfg.texts.ladenBelohnung)} auf dich!${this.cfg.texts.ladenBedingung ? ` <small class="kmg-bedingung">${esc(this.cfg.texts.ladenBedingung)}</small>` : ''}` : ''}${ld.adresse ? `<br>${esc(ld.adresse)}` : ''}${ld.zeiten ? `<br>${esc(ld.zeiten)}` : ''}</p><div class="kmg-reihe kmg-reihe--links"><a class="kmg-btn" href="${esc(ld.url)}" data-stat="laden">Zum Mühlenladen</a>${ld.karte ? `<a class="kmg-btn kmg-btn--leise" href="${esc(ld.karte)}" data-stat="anfahrt" target="_blank" rel="noopener">Anfahrt</a>` : ''}</div></div></div>`
          : '';
      const urkunde = stars && (d.level === 1 || d.level === 5) ? `<button type="button" class="kmg-btn" data-act="urkunde">Urkunde drucken</button>` : '';
      const nextLvl = d.level < 5 && this.s.level > d.level;
      let lob = stars === 3 ? 'Wahnsinn, drei Sterne! Du bist ein echter Müller.' : stars ? 'Feierabend! Gut gemacht – morgen wird’s noch besser.' : 'Feierabend! Probier’s gleich nochmal, Krümel hilft dir.';
      if (unlocked) lob += ` Und: ${LEVELS[d.level + 1].jahr} ist jetzt frei!`;
      this.hud.hidden = true;
      this.closePanel();
      this.show(`<div class="kmg-ende">
        <h2 class="kmg-titel">Feierabend!</h2>
        <p class="kmg-sterne kmg-sterne--gross" aria-label="${stars} von 3 Sternen">${[1, 2, 3].map((i) => ART.stern(i <= stars)).join('')}</p>
        ${this.bubble('kruemel', lob, stars ? 'happy' : 'oh')}
        <ul class="kmg-kriterien" role="list">${crit}</ul>
        <div class="kmg-reihe">${nextLvl ? `<button type="button" class="kmg-btn kmg-btn--gross${unlocked ? ' is-neu' : ''}" data-act="level" data-level="${d.level + 1}" data-focus>Weiter: ${esc(LEVELS[d.level + 1].jahr)}</button>` : ''}<button type="button" class="kmg-btn${nextLvl ? '' : ' kmg-btn--gross'}" data-act="level" data-level="${d.level}" ${nextLvl ? '' : 'data-focus'}>Nochmal spielen</button>${urkunde}<button type="button" class="kmg-btn kmg-btn--leise" data-act="menu">Menü</button></div>
        <p class="kmg-leise kmg-unten-hinweis">Unter dem Spiel: Tipps zum echten Backen und ein Ausmalbild ↓</p>
      </div>`);
      const muehlen = d.level === 5 && stars ? `<p class="kmg-lead">Gab es 1950 noch rund 19.000 Mühlen in Deutschland, waren es 2020 nur noch 550. Du hilfst mit, dass eine davon weiter mahlt!</p>` : '';
      const ausmalen = `<div class="kmg-bruecke kmg-bruecke--laden">${ART.kruemel()}<div><h4>Krümel und Plitsch zum Ausmalen</h4><p>Ein Ausmalbild mit der Mühle, Krümel und Plitsch – zum Drucken oder als Bild.</p><div class="kmg-reihe kmg-reihe--links"><button type="button" class="kmg-btn" data-act="ausmalen">Ausmalbild öffnen</button></div></div></div>`;
      this.showBelow(`<h2 class="kmg-unten__titel">Nach Feierabend</h2>${muehlen}${backen}${malbuch}${ausmalen}${laden}`, false);
      if (stars === 3 || unlocked) this.confetti();
      this.lastDay = d;
      this.day = null;
      this.root.scrollIntoView({ block: 'nearest', behavior: reduced() ? 'auto' : 'smooth' });
    }

    // Mehlwolke mit Konfetti in den Hausfarben (bei drei Sternen oder einem neuen Level)
    confetti() {
      if (reduced()) return;
      const box = document.createElement('div');
      box.className = 'kmg-konfetti';
      box.setAttribute('aria-hidden', 'true');
      const tones = ['fg', 'fs', 'fb', 'fz', 'fe', 'fm'];
      box.innerHTML =
        '<span class="kmg-mehlwolke"></span>' +
        Array.from({ length: 36 }, (_, i) => `<i class="${tones[i % tones.length]}" style="--x:${(i * 37) % 100}%;--d:${(i % 7) * 0.12}s;--r:${(i * 53) % 360}deg"></i>`).join('');
      this.root.appendChild(box);
      setTimeout(() => box.remove(), 3600);
    }

    async act_malbuch(btn) {
      const mb = this.cfg.malbuch;
      if (this.s.malbuch || typeof window.kmAddItems !== 'function') return;
      btn.disabled = true;
      btn.textContent = 'Einen Moment …';
      const res = await window.kmAddItems([{ id: mb.variant, q: 1, t: mb.title }], { source: 'km-muehlenspiel' });
      if (res && res.added) {
        this.s.malbuch = true;
        this.persist();
        this.track('malbuch');
        btn.textContent = 'Liegt im Warenkorb';
      } else {
        btn.disabled = false;
        btn.textContent = 'Nochmal versuchen';
      }
    }

    /* ----- Sammelkarten, Eltern-Infos, Urkunde ----- */
    act_karten() {
      const list = CARDS.map((c) => {
        const has = this.s.cards.includes(c.id);
        return `<li class="kmg-sammel${has ? '' : ' is-zu'}">${has ? ART.grain(c.id) : svg('0 0 40 40', '<text class="fh kmg-svgtext" x="20" y="27" text-anchor="middle">?</text>', 'kmg-ico')}<b>${has ? esc(c.name) : '???'}</b><span>${has ? esc(c.text) : c.ur ? 'Erfülle einen Tagesauftrag' : 'Mahle dieses Getreide'}</span></li>`;
      }).join('');
      this.showBelow(`<h2 class="kmg-unten__titel">Sammelkarten</h2><p class="kmg-leise">Jedes Getreide, das du mahlst, kommt ins Album. Urgetreide gibt es für den goldenen Tagesauftrag – jeden Tag einen.</p><ul class="kmg-album" role="list">${list}</ul><h4 class="kmg-h kmg-h--klein">Unsere Bauern</h4><ul class="kmg-bauern" role="list">${FARMERS.map((f) => `<li><b>${esc(f.name)}</b><span>${esc(f.kreis)}, ${f.km} km</span>${ART.herzen(this.hearts(f))}</li>`).join('')}</ul><button type="button" class="kmg-btn kmg-btn--gross" data-act="nachOben">Zurück zum Spiel</button>`);
    }
    act_hilfe() {
      const step = (pic, title, text) => `<li>${pic}<span><b>${title}</b><br>${text}</span></li>`;
      const inGame = this.day && !this.day.over;
      this.showBelow(
        `<h2 class="kmg-unten__titel">So geht’s</h2><ol class="kmg-anleitung">
          ${step(ART.traktor(), 'Korn annehmen', 'Ein Bauer kommt: Tipp auf die Annahme und zieh eine Probe. Bis 14,5 % Feuchte darf das Korn ins Silo, nasses Korn geht zurück.')}
          ${step(svg('0 0 24 24', '<path class="fl ol" d="M4 16q-2-8 6-11 8-2 11 4 2 9-6 11-9 1-11-4z"/>', 'kmg-ico'), 'Reinigen', 'Tipp alle Steinchen, Strohhalme und dunklen Körner weg.')}
          ${step(ART.bake('semmel'), 'Bestellung wählen', 'Tipp auf eine Bestellung und such das passende Korn aus dem Silo aus. Dinkel wird vorher geschält.')}
          ${step(ART.walzen(), 'Mahlen', 'Tipp fünfmal auf den Walzenstuhl. Dafür braucht er Strom vom Wasserrad.')}
          ${step(`<span class="kmg-skala__farbe f-40"></span>`, 'Sieben', 'Jedes Sieben macht das Mehl heller: Vollkorn ist ungesiebt, Type 405 ist dreimal gesiebt.')}
          ${step(ART.pack('sack'), 'Abfüllen', 'Bäckereien brauchen einen Sack oder BigBag, Familien und Pakete eine Tüte.')}
          ${step(svg('0 0 60 40', '<g class="o"><path class="fh" d="M6 10h40v20H6z"/><circle class="fn" cx="16" cy="32" r="5"/><circle class="fn" cx="36" cy="32" r="5"/></g>', 'kmg-ico'), 'Reste wegbringen', 'Ab 3 Kleie fährt der Anhänger die Reste zu den Tieren.')}
        </ol>
        <p class="kmg-anleitung__sterne">${ART.stern()} <span>Drei Sterne gibt es für <b>glückliche Kunden</b>, <b>Korn aus der Region</b> und <b>verwertete Reste</b>. ${ART.herzen(3)} Bauern, die du gut behandelst, kommen öfter und bringen trockeneres Korn. Im Menü wählst du die Spielart: <b>Kleiner Müller</b> spielt ohne Uhr und zeigt mit dem Finger, was als Nächstes dran ist; <b>Meister-Müller</b> ist schneller und ohne Hilfen.</span></p>
        <div class="kmg-reihe"><button type="button" class="kmg-btn kmg-btn--gross" data-act="nachOben">${inGame ? 'Zurück zum Spiel' : 'Alles klar!'}</button>${inGame ? '' : '<button type="button" class="kmg-btn kmg-btn--leise" data-act="introOben">Geschichte nochmal ansehen</button>'}</div>`
      );
    }
    act_introOben() {
      this.act_nachOben();
      this.act_intro();
    }
    act_panelZu() {
      this.panelEl.hidden = true;
      this.holding = false;
    }
    act_eltern() {
      const l = this.cfg.links || {};
      const links = [
        l.mehltypen && `<li><a href="${esc(l.mehltypen)}">Das Einmaleins der Mehltypen</a></li>`,
        l.geschichte && `<li><a href="${esc(l.geschichte)}">Die Geschichte der Kunstmühle</a></li>`,
        l.technik && `<li><a href="${esc(l.technik)}">So arbeitet die Mühle</a></li>`,
        this.cfg.laden?.url && `<li><a href="${esc(this.cfg.laden.url)}">Unser Mühlenladen in Fahlenbach</a></li>`,
        `<li><button type="button" class="kmg-linkknopf" data-act="ausmalen">Ausmalbild mit Krümel und Plitsch</button></li>`,
      ]
        .filter(Boolean)
        .join('');
      this.showBelow(
        `<h2 class="kmg-unten__titel">Für Erwachsene</h2><div class="kmg-text">${this.cfg.eltern || ''}</div>${links ? `<ul class="kmg-links">${links}</ul>` : ''}<p class="kmg-leise">Der Spielstand bleibt nur in deinem Browser, das Spiel setzt keine Cookies.${this.cfg.statistik ? ' Wenn du im Cookie-Banner der Statistik zustimmst, zählen wir anonym mit, wie weit und wie lange gespielt wird – ohne Namen und ohne Kundendaten.' : ' Es sammelt keine Daten.'}</p><div class="kmg-reihe"><button type="button" class="kmg-btn kmg-btn--gross" data-act="nachOben">Zurück zum Spiel</button><button type="button" class="kmg-btn kmg-btn--leise" data-act="reset">Spielstand löschen</button></div>`
      );
    }
    act_reset(btn) {
      if (btn.dataset.sicher) {
        this.s = { level: 1, stars: {}, cards: [], ur: 0, factIdx: 0, sound: this.s.sound };
        this.persist();
        this.act_nachOben();
        return this.menu();
      }
      btn.dataset.sicher = '1';
      btn.textContent = 'Wirklich löschen?';
    }

    act_urkunde() {
      const name = this.s.name || '';
      this.panel(
        `<h3 class="kmg-h">Deine Urkunde</h3>${this.bubble('plitsch', 'Wie heißt du? Dein Name kommt auf die Urkunde.')}<label class="kmg-feldname"><span>Name</span><input type="text" maxlength="30" autocomplete="off" value="${esc(name)}" data-name data-focus></label><div class="kmg-reihe"><button type="button" class="kmg-btn kmg-btn--gross" data-act="drucken">Drucken</button><button type="button" class="kmg-btn kmg-btn--leise" data-act="panelZu">Zurück</button></div>`,
        { hold: false }
      );
    }
    // Urkunde als eigenes SVG (A4-Format): dient als Vorschau, Druckvorlage und Bild zum Speichern
    urkundeSvg() {
      const top = [5, 4, 3, 2, 1].find((l) => this.s.stars[l]) || 1;
      const stars = Object.values(this.s.stars).reduce((a, b) => a + b, 0);
      const date = new Date().toLocaleDateString('de-DE', { day: 'numeric', month: 'long', year: 'numeric' });
      const jahr = LEVELS[top].jahr === 'Heute' ? 'in die Mühle von heute' : `ins Jahr ${LEVELS[top].jahr}`;
      const name = this.s.name || 'Unser kleiner Müller';
      const nameSize = Math.min(54, Math.round(1150 / Math.max(10, name.length)));
      // Belohnung im Mühlenladen (Section-Einstellung); leer = kein Hinweis
      const belohnung = (this.cfg.texts?.ladenBelohnung || '').trim();
      const bedingung = (this.cfg.texts?.ladenBedingung || '').trim();
      // Eingebettete Zeichnungen ohne ihre Spiel-Klasse, sonst überschreibt das Stylesheet Größe und Lage
      const nest = (str, x, y, w, h) => str.replace(/^\s*<svg class="[^"]*"/, `<svg x="${x}" y="${y}" width="${w}" height="${h}"`);
      const t = (x, y, size, weight, fill, text, anchor = 'middle') =>
        `<text x="${x}" y="${y}" font-size="${size}" font-weight="${weight}" text-anchor="${anchor}" style="fill:${fill}">${esc(text)}</text>`;
      const ink = 'rgb(var(--km-walnuss))';
      const quiet = 'rgb(var(--km-holz))';
      return `<svg class="km-urkunde-svg" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 840 1188" width="840" height="1188" role="img" aria-label="Urkunde für ${esc(name)}">
        <rect width="840" height="1188" class="fm"/>
        <rect x="22" y="22" width="796" height="1144" rx="10" style="fill:none;stroke:rgb(var(--km-gold));stroke-width:6"/>
        <rect x="36" y="36" width="768" height="1116" rx="6" style="fill:none;stroke:rgb(var(--km-gold));stroke-width:2"/>
        ${nest(SCENE(top, ''), 70, 66, 700, 252)}
        ${t(420, 372, 18, 400, quiet, 'Kunstmühle Hofmeir · Fahlenbach in der Hallertau')}
        ${t(420, 468, 76, 700, ink, 'Urkunde')}
        ${t(420, 566, nameSize, 400, ink, name)}
        <path d="M170 592h500" style="fill:none;stroke:${quiet};stroke-width:1.5"/>
        ${t(420, 646, 20, 400, ink, 'hat Korn von den Bauern angenommen, zu Mehl gemahlen und gesiebt')}
        ${t(420, 680, 20, 400, ink, `und ist bis ${jahr} gekommen.`)}
        ${nest(ART.stern(), 300, 718, 40, 40)}
        ${t(352, 748, 26, 700, ink, `${stars} Sterne gesammelt`, 'start')}
        ${nest(ART.kruemel(), 250, 790, 170, 208)}
        ${nest(ART.plitsch(), 470, 850, 110, 145)}
        ${
          belohnung
            ? t(420, 1012, 17, 700, 'rgb(var(--km-ziegel))', 'Komm selbst in den Mühlenladen und zeig uns deine Urkunde –') +
              t(420, 1035, 17, 700, 'rgb(var(--km-ziegel))', `dann wartet ${belohnung} auf dich!`) +
              (bedingung ? t(420, 1058, 13, 400, quiet, bedingung) : '')
            : ''
        }
        <path d="M120 1086h240M480 1086h240" style="fill:none;stroke:${quiet};stroke-width:1.5"/>
        ${t(240, 1108, 17, 400, quiet, 'Kater Krümel')}
        ${t(600, 1108, 17, 400, quiet, 'Wassertropfen Plitsch')}
        ${t(420, 1138, 15, 400, quiet, `${date} · www.kunstmuehle.de`)}
      </svg>`;
    }

    // Farben aus den CSS-Klassen fest ins SVG schreiben, damit es auch als Bild stimmt
    inlineSvg(root) {
      const props = ['fill', 'stroke', 'stroke-width', 'stroke-linecap', 'stroke-linejoin', 'stroke-dasharray', 'opacity', 'fill-opacity', 'stroke-opacity'];
      const font = getComputedStyle(document.body).getPropertyValue('--font-heading-family') || 'sans-serif';
      root.querySelectorAll('*').forEach((el) => {
        const cs = getComputedStyle(el);
        props.forEach((prop) => el.setAttribute(prop, cs.getPropertyValue(prop)));
        if (el.tagName === 'stop') el.setAttribute('stop-color', cs.getPropertyValue('stop-color'));
        if (el.tagName === 'text') {
          el.setAttribute('font-family', font.trim() || 'sans-serif');
          el.setAttribute('font-size', cs.getPropertyValue('font-size'));
          el.setAttribute('font-weight', cs.getPropertyValue('font-weight'));
          el.setAttribute('letter-spacing', cs.getPropertyValue('letter-spacing'));
        }
      });
    }

    act_drucken() {
      const input = this.panelInner.querySelector('[data-name]');
      // Der Name bleibt nur im Browser
      this.s.name = (input?.value || '').trim().slice(0, 30);
      this.persist();
      this.act_panelZu();
      this.openSheet(this.urkundeSvg(), { label: 'Deine Urkunde', file: 'urkunde-der-kleine-mueller.png', title: 'Meine Müller-Urkunde', back: '[data-act="urkunde"]', stat: 'urkunde', hoechstes: this.s.level });
    }

    // Ausmalbild: Mühle, Krümel und Plitsch als Linienzeichnung (Füllungen weiß über .km-ausmal)
    ausmalSvg() {
      const nest = (str, x, y, w, h) => str.replace(/^\s*<svg class="[^"]*"/, `<svg x="${x}" y="${y}" width="${w}" height="${h}"`);
      return `<svg class="km-urkunde-svg km-ausmal" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 840 1188" width="840" height="1188" role="img" aria-label="Ausmalbild mit Krümel und Plitsch">
        <rect width="840" height="1188" class="fw"/>
        <text x="420" y="84" font-size="34" font-weight="700" text-anchor="middle" style="fill:rgb(var(--km-walnuss))">Male die Kunstmühle aus!</text>
        ${nest(SCENE(5, ''), 40, 120, 760, 273)}
        ${nest(ART.kruemel(), 120, 470, 340, 416)}
        ${nest(ART.plitsch(), 500, 600, 230, 302)}
        <path d="M60 940h720" style="fill:none;stroke:rgb(var(--km-walnuss));stroke-width:2"/>
        <text x="80" y="1060" font-size="24" text-anchor="start" style="fill:rgb(var(--km-walnuss))">Gemalt von:</text>
        <path d="M230 1064h400" style="fill:none;stroke:rgb(var(--km-holz));stroke-width:1.5"/>
        <text x="420" y="1134" font-size="16" text-anchor="middle" style="fill:rgb(var(--km-holz))">Kater Krümel und Wassertropfen Plitsch · Kunstmühle Hofmeir · www.kunstmuehle.de</text>
      </svg>`;
    }
    act_ausmalen() {
      this.openSheet(this.ausmalSvg(), { label: 'Ausmalbild', file: 'ausmalbild-kunstmuehle.png', title: 'Ausmalbild Kunstmühle', stat: 'ausmalbild' });
    }

    // Blatt-Vorschau mit Drucken und „Als Bild speichern“ (Urkunde, Ausmalbild)
    openSheet(svgString, opts) {
      const opener = document.activeElement;
      document.querySelector('.km-urkunde-overlay')?.remove();
      const ov = document.createElement('div');
      ov.className = 'km-urkunde-overlay';
      ov.setAttribute('role', 'dialog');
      ov.setAttribute('aria-modal', 'true');
      ov.setAttribute('aria-label', opts.label);
      ov.innerHTML = `<div class="km-urkunde-blatt">${svgString}</div><div class="km-urkunde-knoepfe"><button type="button" class="kmg-btn kmg-btn--gross" data-u="drucken">Drucken</button><button type="button" class="kmg-btn" data-u="bild">Als Bild speichern</button><button type="button" class="kmg-btn kmg-btn--leise" data-u="zu">Schließen</button></div>`;
      document.body.appendChild(ov);
      document.body.classList.add('km-urkunde-offen');
      const svgEl = ov.querySelector('svg');
      this.inlineSvg(svgEl);
      const stat = (art) => this.track(`${opts.stat}_${art}`, opts.hoechstes ? { hoechstes: opts.hoechstes } : {});
      stat('geoeffnet');
      const close = () => {
        ov.remove();
        document.body.classList.remove('km-urkunde-offen');
        document.removeEventListener('keydown', onKey);
        const back = (opts.back && this.root.querySelector(opts.back)) || (opener && opener.isConnected ? opener : null);
        if (back) back.focus({ preventScroll: true });
      };
      const onKey = (e) => e.key === 'Escape' && close();
      document.addEventListener('keydown', onKey);
      ov.addEventListener('click', async (e) => {
        const b = e.target.closest('[data-u]');
        if (!b) return;
        if (b.dataset.u === 'zu') return close();
        if (b.dataset.u === 'drucken') {
          stat('gedruckt');
          return window.print();
        }
        b.disabled = true;
        const label = b.textContent;
        b.textContent = 'Einen Moment …';
        try {
          await this.saveImage(svgEl, opts.file, opts.title);
          stat('bild');
        } catch (err) {
          b.textContent = 'Ging leider nicht';
          setTimeout(() => (b.textContent = label), 2500);
          b.disabled = false;
          return;
        }
        b.textContent = label;
        b.disabled = false;
      });
      ov.querySelector('[data-u="drucken"]').focus();
    }

    async saveImage(svgEl, fileName = 'urkunde-der-kleine-mueller.png', title = 'Meine Müller-Urkunde') {
      const xml = new XMLSerializer().serializeToString(svgEl);
      const img = new Image();
      img.src = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(xml)}`;
      await img.decode();
      const canvas = document.createElement('canvas');
      canvas.width = 1680;
      canvas.height = 2376;
      const ctx = canvas.getContext('2d');
      ctx.fillStyle = '#fff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      const blob = await new Promise((r) => canvas.toBlob(r, 'image/png'));
      // Auf dem Handy: Teilen-Dialog (Fotos, WhatsApp …), sonst Download
      if (window.File && navigator.canShare) {
        const file = new File([blob], fileName, { type: 'image/png' });
        if (navigator.canShare({ files: [file] })) {
          try {
            await navigator.share({ files: [file], title });
            return;
          } catch (e) {
            if (e.name === 'AbortError') return;
          }
        }
      }
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(a.href), 5000);
    }
  }

  customElements.define('km-muehlenspiel', KmMuehlenspiel);
})();
